import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canCreate, canUpdate, canDelete } from "../../Services/pageAccess.js";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowLeft, ArrowUp, Calendar, CheckCircle2, ChevronLeft, ChevronRight, Clock, Globe, MoreHorizontal, Plus, RefreshCw, Save, Search, SlidersHorizontal, Star, Trash2, Users, Video } from "lucide-react";

import { apiGet, apiPatch, apiPost, apiDelete, getApiSettings } from "../../Api/Client.js";
import { ResearchProspectButton } from "./ProspectDossier";
import { CheckboxDropdown, EmptyState, Modal } from "../../Shared/ScreenComponents.jsx";
import "../../Styles/LmsScreen.css";
import {
  formatDate,
  formatDateTime,
  recentPast,
  normalizeLeadSource,
  sourceTone,
  uniqueOptions,
} from "../../Shared/ScreenUtils.jsx";
import { errorText } from "../../Api/errorText.js";
import { createLatestGate } from "../../Shared/latestRequest.js";
import { bulkOutcomeMessage, runForEach } from "./bulkRun.js";
import {
  buildLeadDraft,
  changedLeadPayload,
  isValidContactName,
  leadCaptureMoneyFields,
  moneyInput,
  parseChecklist,
  serializeChecklist,
  shownStage,
  tagIdsForLead,
  toDateTimePayload,
} from "./lmsLeadForm.js";
import { LeadCallContextPanel, recordingDate } from "./LeadCallContext.jsx";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Channel outcomes logged alongside a note. Rebuilt 2026-07-15 as a compact
// horizontal chip row (was a 5-row collapsible checkbox+dropdown block). Only
// the outcomes a BA actually needs remain:
//   - meeting / email: on-off toggle (green dot when on)
//   - call / whatsapp:  connected | no_answer (half-tone dot + tiny dropdown)
// Stored in note.metadata.channel_status = { call: "connected", ... }.
const NOTE_CHANNEL_CHIPS = [
  { key: "meeting", label: "Meeting done", kind: "toggle", on: "done", tone: "green" },
  { key: "call", label: "Call", kind: "outcome" },
  { key: "whatsapp", label: "WhatsApp", kind: "outcome" },
  { key: "email", label: "Email", kind: "toggle", on: "sent", tone: "blue" },
];
const OUTCOME_OPTIONS = [["connected", "Connected"], ["no_answer", "No Answer"]];

function NoteChannelChips({ value = {}, onChange }) {
  const [openKey, setOpenKey] = useState(null);
  const setStatus = (key, status) => {
    const next = { ...value };
    if (!status) delete next[key]; else next[key] = status;
    onChange(next);
  };
  return (
    <div className="Lms-Chan-Chips" role="group" aria-label="Channel outcome">
      {NOTE_CHANNEL_CHIPS.map((chip) => {
        const active = value[chip.key];
        if (chip.kind === "toggle") {
          return (
            <button
              key={chip.key} type="button"
              className={`Lms-Chan-Chip ${chip.tone}${active ? " on" : ""}`}
              aria-pressed={Boolean(active)}
              onClick={() => setStatus(chip.key, active ? "" : chip.on)}
            >
              <span className="Lms-Chan-Dot" />{chip.label}
            </button>
          );
        }
        // Grey until selected (like the toggle chips). Main click cycles
        // neutral -> Connected (green) -> No Answer (red) -> neutral. The caret
        // still lets you jump straight to an outcome.
        const toneClass = active === "connected" ? "green" : active === "no_answer" ? "red" : "";
        const cycle = { "": "connected", connected: "no_answer", no_answer: "" };
        return (
          <span key={chip.key} className={`Lms-Chan-Chip outcome ${toneClass}${active ? " on" : ""}`}>
            <button type="button" className="Lms-Chan-Main" aria-pressed={Boolean(active)}
              onClick={() => setStatus(chip.key, cycle[active || ""])}>
              <span className="Lms-Chan-Dot" />{chip.label}{active ? ` — ${active === "connected" ? "Connected" : "No Answer"}` : ""}
            </button>
            <button type="button" className="Lms-Chan-Caret" aria-label={`${chip.label} outcome`} onClick={() => setOpenKey(openKey === chip.key ? null : chip.key)}>▾</button>
            {openKey === chip.key && (
              <span className="Lms-Chan-Menu" onMouseLeave={() => setOpenKey(null)}>
                {OUTCOME_OPTIONS.map(([val, lbl]) => (
                  <button key={val} type="button" className={active === val ? "sel" : ""} onClick={() => { setStatus(chip.key, val); setOpenKey(null); }}>{lbl}</button>
                ))}
                {active && <button type="button" className="clear" onClick={() => { setStatus(chip.key, ""); setOpenKey(null); }}>Clear</button>}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── Note tiering ──────────────────────────────────────────────────────────
// Classify a note into one of three weights WITHOUT rewriting its data:
//   meeting → tier 1 (elevated card, the thing you want to see)
//   call    → tier 2 (a real connected touchpoint, bordered card)
//   dead    → tier 3 (no-answer / DNP / gatekeeper, collapsed to one line)
// Explicit chip metadata wins; otherwise we read the note text with transparent
// keyword rules. Ambiguous-but-substantive notes stay tier 2 so no information
// is ever hidden; only clearly-dead or very short notes collapse to one line.
// Rules validated against 2,124 real open-lead notes from the 2026-07-03 dump.
const RE_ACTIVITY_SUMMARY = /-{2,}\s*activity summary\s*-{2,}[\s\S]*$/i;
const RE_URL = /(https?:\/\/[^\s)]+)/i;
const RE_REC_HOST = /(meet\.atg\.party|screenrec\.com|\/watch|\/meetings\/)/i;
const RE_INTAKE = /^\s*request received from/i;
const RE_MEETING = /\b(meeting done|demo done|meeting completed|demo completed|call done|kt done|walkthrough done|meeting held|had (a|the) (call|meeting|demo)|met (with|the)|solution fit)\b/i;
const RE_MEETING_OTHER = /\b(meeting (scheduled|rescheduled|set|fixed|planned)|demo scheduled|proposal meeting|discovery call|scheduled (a|the) (call|meeting|demo))\b/i;
const RE_CONNECTED = /\b(connected|spoke (to|with)|discussed|client (replied|shared|sent|asked|reverted|confirmed|agreed|joined)|they (replied|shared|confirmed|agreed)|replied|reverted|got back|received (the|an|a )|call back|negotiat|onboarded|will (send|revert|share)|shared (the )?(proposal|quote|jd|mto|comparison|doc|simulation)|interested)/i;
const RE_DEAD = /\b(dnp|did ?not ?pick|no ?pick ?up|no ?answer|not ?reachable|not ?responding|no ?response|switched ?off|out ?of ?(reach|coverage)|gatekeeper|wrong (number|person)|call ?again|call not connecting|ringing|left (a )?message|voicemail|\bvm\b|\bnr\b|unreachable|not ?connected|didn.?t (join|receive|pick)|couldn.?t connect|checking in|busy|reminder sent|dropped (a|an) (msg|mail|email|message)|(email|mail|msg|message|follow.?up) sent asking|follow.?up (email|msg|message)? ?sent)/i;

function noteChannelStatus(note) {
  const m = note.metadata || {};
  return m.channel_status || {};
}
// Body for display: drop the legacy "--- Activity Summary ---" footer (present
// in ~77% of notes) and, when we show a recording button, the bare recording URL.
function cleanNoteBody(note, dropRecUrl) {
  let body = String(noteContent(note) || "").replace(RE_ACTIVITY_SUMMARY, "").trim();
  if (dropRecUrl) {
    body = body.replace(RE_URL, (u) => (RE_REC_HOST.test(u) ? "" : u)).replace(/\s{2,}/g, " ").replace(/^[,\s]+|[,\s]+$/g, "").trim();
  }
  return body;
}
function classifyNote(note) {
  const m = note.metadata || {};
  const cs = noteChannelStatus(note);
  // The recorder is the source of truth for call vs meeting. Trust its stored
  // label whenever we have it — whether the note was created by the recorder
  // integration or a rep pasted a recording link we've since resolved.
  if (m.modality === "call") return "call";
  if (m.modality === "meeting") return "meeting";
  if (m.source === "meet_recorder") return "meeting";
  if (cs.meeting === "done") return "meeting";
  const raw = `${note.title || ""} ${note.body || ""}`;
  const text = raw.replace(RE_ACTIVITY_SUMMARY, "").trim();
  if (RE_INTAKE.test(text)) return "dead";
  const hasRec = RE_REC_HOST.test(raw);
  if (cs.call === "connected" || cs.whatsapp === "connected") return "call";
  // A pasted recorder link is NOT itself a meeting signal — the /meetings/ path
  // serves calls too. Only the note's own words (or stored modality above) decide;
  // a bare link with no meeting language defaults to the minimal call tier.
  if (hasRec) return RE_MEETING.test(text) ? "meeting" : "call";
  if (RE_MEETING.test(text)) return "meeting";
  if (cs.call === "no_answer" || cs.whatsapp === "no_answer") return "dead";
  if (RE_DEAD.test(text) && !RE_CONNECTED.test(text)) return "dead";
  if (RE_CONNECTED.test(text) || RE_MEETING_OTHER.test(text)) return "call";
  return text.replace(/\s+/g, " ").length <= 24 ? "dead" : "call";
}
function noteRecordingLink(note) {
  const m = note.metadata || {};
  let url = m.recording_url || null;
  if (!url) {
    const match = `${note.body || ""}`.match(RE_URL);
    url = match && RE_REC_HOST.test(match[1]) ? match[1] : null;
  }
  if (!url) return null;
  // A call opens on the recorder's audio-first /call/<id> view; rewrite the
  // legacy /meetings/ path so the address bar reflects that it's a call. The
  // two routes render the same recording, so old links keep working either way.
  if (m.modality === "call") url = url.replace(/\/meetings\//, "/call/");
  return url;
}
// A compact one-line summary for dead touches: channel + outcome + next step,
// derived from what's already stored (never invented).
function noteOneLiner(note) {
  const cs = noteChannelStatus(note);
  const parts = [];
  const outcome = (s) => (s === "no_answer" ? "No answer" : s === "connected" ? "Connected" : "");
  if (cs.call) parts.push(`Call — ${outcome(cs.call)}`);
  if (cs.whatsapp) parts.push(`WhatsApp — ${outcome(cs.whatsapp)}`);
  if (cs.email) parts.push("Email sent");
  const body = cleanNoteBody(note, false).replace(/\s+/g, " ").trim();
  if (body && parts.length === 0) parts.push(body.length > 90 ? `${body.slice(0, 88)}…` : body);
  else if (body) parts.push(body.length > 60 ? `${body.slice(0, 58)}…` : body);
  return parts.join(" · ") || "Touch logged";
}
function firstNameOf(name) {
  return String(name || "").trim().split(/\s+/)[0] || "";
}

// Only datasets this screen actually reads. learningAssignments /
// leadQueueSnapshots / revenueSnapshots were refetched on every refresh()
// (including after every assign / stage move) without being used anywhere.
const LEAD_LIST_KEYS = ["lmsLeads", "leadTags", "employees"];
// Opening a lead is served entirely by the per-lead endpoint (/Lms/lms/lead/<id>/,
// loaded into `externalLead`), which returns that one lead's notes, contacts,
// proposals, audits and activities already scoped and trimmed to the newest 20.
// The detail refresh USED to also bulk-fetch every one of those tables in FULL —
// all ~15k notes, every contact/activity across all leads, tens of MB — and filter
// them in the browser. Nothing reads those globals on the detail view anymore (the
// memo that once did is gated on `leadAccounts`, which is never fetched), so the
// detail view needs only the same lightweight keys as the list. This is the fix for
// "the CRM got slow again": a lead now opens without downloading the whole system.
const LEAD_DETAIL_KEYS = [...LEAD_LIST_KEYS];

// Funnel-phase colors: blue = fresh, indigo = being worked, gold = momentum,
// green = won, red = lost, slate = parked. One glance = how hot the lead is.
function stageTone(value = "") {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("won") || normalized.includes("confirmed")) return "green";
  if (normalized.includes("lost") || normalized.includes("negative")) return "red";
  if (normalized.includes("cold") || normalized.includes("nurture") || normalized.includes("recycle")) return "slate";
  if (normalized.includes("proposal") || normalized.includes("discovery") || normalized.includes("demo")) return "gold";
  if (normalized.includes("contact") || normalized.includes("engaged")) return "indigo";
  if (normalized.includes("new")) return "blue";
  return "slate";
}

// "2h Ago" beats "08 Jul 2026" when scanning a lead list.
function timeAgo(iso) {
  if (!iso) return "–";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "–";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "Just Now";
  if (mins < 60) return `${mins}m Ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h Ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 15) return `${days}d Ago`;
  const opts = { day: "numeric", month: "short" };
  if (then.getFullYear() !== new Date().getFullYear()) opts.year = "2-digit";
  return then.toLocaleDateString("en-IN", opts);
}

// Future-facing twin of timeAgo for follow-up dates.
function friendlyDay(iso) {
  if (!iso) return "-";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "-";
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(then) - startOfDay(new Date())) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 1 && diffDays < 15) return `In ${diffDays}d`;
  if (diffDays < -1 && diffDays > -15) return `${-diffDays}d Overdue`;
  const opts = { day: "numeric", month: "short" };
  if (then.getFullYear() !== new Date().getFullYear()) opts.year = "2-digit";
  return then.toLocaleDateString("en-IN", opts);
}

function sourceLogoNode(label = "") {
  const tone = sourceTone(label);
  if (tone === "client") return <Globe size={16} />;
  if (tone === "linkedin") return <Users size={16} />;
  if (tone === "instagram") return <span>IG</span>;
  if (tone === "banao") return <span>B</span>;
  if (tone === "interviewgod") return <span>IG</span>;
  if (tone === "twitter") return <span>X</span>;
  if (tone === "github") return <span>GH</span>;
  if (tone === "vikaas") return <span>V</span>;
  return <span>{String(label || "?").slice(0, 2).toUpperCase()}</span>;
}

const PRODUCT_OPTIONS = ["Banao", "InterviewGod", "Vidya", "Vikaas"];
const CHANNEL_OPTIONS = ["Website-inbound", "LinkedIn-outreach", "Event", "Referral"];

// Product letter-marks (placeholder until real logos land — swap the glyphs here).
const PRODUCT_BADGES = {
  Banao: { glyph: "B", cls: "banao" },
  InterviewGod: { glyph: "IG", cls: "ig" },
  Vidya: { glyph: "Vd", cls: "vidya" },
  Vikaas: { glyph: "Vk", cls: "vikaas" },
};

function ProductBadge({ product }) {
  const badge = PRODUCT_BADGES[product] || { glyph: "?", cls: "unknown" };
  return <span className={`Lms-Product-Badge ${badge.cls}`} title={product || "Product Not Set"}>{badge.glyph}</span>;
}

// Channel as a single glyph — scan without reading; hover for channel + campaign.
function ChannelIcon({ channel, campaign }) {
  const tip = (label) => `${label}${campaign ? ` · ${campaign}` : ""}`;
  if (channel === "Website-inbound") return <span className="Lms-Channel-Icon web" title={tip("Website-Inbound")} aria-label={tip("Website-Inbound")}><Globe size={14} /></span>;
  if (channel === "LinkedIn-outreach") return <span className="Lms-Channel-Icon linkedin" title={tip("LinkedIn-Outreach")} aria-label={tip("LinkedIn-Outreach")}>in</span>;
  if (channel === "Event") return <span className="Lms-Channel-Icon event" title={tip("Event")} aria-label={tip("Event")}><Calendar size={14} /></span>;
  if (channel === "Referral") return <span className="Lms-Channel-Icon referral" title={tip("Referral")} aria-label={tip("Referral")}><Users size={14} /></span>;
  return <span className="Lms-Channel-Icon unknown" title="Channel Not Set">–</span>;
}

// ── Note display helpers ─────────────────────────────────────────────────────
// Note titles are deprecated. Old data is messy: the legacy importer baked the
// author + date into the title ("Legacy Note By {name} On {date}") and left the
// author FK empty, and app-created notes default the title to "General Update".
// These helpers collapse a note into ONE clean content string + recover the real
// author from the legacy title, so cards read as a single intuitive block.
function parseLegacyNoteAuthor(title) {
  const match = /^Legacy(?: Test)? Note By (.+?)(?: On | \(|$)/i.exec(String(title || ""));
  return match ? match[1].trim() : "";
}

function isBoilerplateNoteTitle(title) {
  const value = String(title || "").trim();
  if (!value) return true;
  if (/^general update$/i.test(value)) return true;      // app default, not real content
  if (/^legacy(?: test)? note/i.test(value)) return true; // importer auto-title
  return false;
}

// One readable block: drop boilerplate titles, otherwise fold a meaningful title
// into the body (title+description merged) without duplicating it.
function noteContent(note) {
  const title = String(note?.title || "").trim();
  const body = String(note?.body || "").trim();
  if (isBoilerplateNoteTitle(title)) return body;
  if (!body) return title;
  if (body.toLowerCase().startsWith(title.toLowerCase())) return body;
  return `${title} — ${body}`;
}

// One-line "what happened" for the note-count hover — a recorder note's raw
// body is a structured dump (title/date/recording link/summary/action items);
// showing that raw text (or the recording link) in a table-scan hover is noise.
// Pull out just the AI summary line instead. Any other note keeps using the
// existing noteOneLiner() distillation, unchanged.
// Front-view timeline date — day + month only. The year shows up in the
// hover tooltip instead (dateFullHover), never on the row itself.
function dateNoYear(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return recentPast(value, date) || date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function dateFullHover(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
// First action item off a recorder note's raw body, for the one-line "→ next
// step" — same source as the AI Analysis tab, just the first line of it.
function firstActionItem(note) {
  const match = String(note.body || "").match(/Action Items:\s*\n?-\s*([^\n]+)/);
  if (match) return match[1].trim();
  // Pasted-link call whose action item was imported from the recorder into metadata.
  const acts = note.metadata?.recording_actions;
  if (Array.isArray(acts) && acts.length) return String(acts[0]).trim();
  return "";
}

function hoverOneLiner(note) {
  const m = note.metadata || {};
  if (m.source === "meet_recorder") {
    const label = m.modality === "call" ? "Call" : "Meeting";
    // The rep's own words about the call, when there are any, ARE the note —
    // the AI summary is the bonus on top, not a replacement for it. Show the
    // rep's note the same way an ordinary note shows its own text, so a call
    // with a recording reads in the timeline exactly like one without.
    if (m.rep_note) {
      const repText = String(m.rep_note).replace(/\s+/g, " ").trim();
      return `${label} — ${repText.length > 220 ? `${repText.slice(0, 218)}…` : repText}`;
    }
    const body = String(note.body || "");
    const match = body.match(/Summary:\s*([\s\S]*?)(?:\n\nAction Items:|$)/);
    const summary = (match ? match[1] : "").replace(/\s+/g, " ").trim();
    const clean = summary && !/^no summary/i.test(summary) ? summary : "No summary yet";
    return `${label} — ${clean.length > 220 ? `${clean.slice(0, 218)}…` : clean}`;
  }
  // Not a recorder-attached note, but someone pasted a recording link straight
  // into a plain note as free text — same "raw URL in the hover" problem, just
  // via a different path. Strip the link; show whatever real text is left.
  const recLink = noteRecordingLink(note);
  if (recLink) {
    // cleanNoteBody strips whatever recorder URL is in the body (regardless of
    // any /call/ rewrite on the link we render), leaving any real note text.
    const withoutLink = cleanNoteBody(note, true).replace(/\s+/g, " ").trim();
    if (withoutLink) return withoutLink.length > 220 ? `${withoutLink.slice(0, 218)}…` : withoutLink;
    // Bare pasted link: show the recorder's own summary if we imported one, else
    // just name the recording — never the useless "Recording shared".
    const label = m.modality === "meeting" ? "Meeting" : "Call";
    const summary = String(m.recording_summary || "").replace(/\s+/g, " ").trim();
    if (summary) return `${label} — ${summary.length > 220 ? `${summary.slice(0, 218)}…` : summary}`;
    if (m.recording_title) return `${label} — ${m.recording_title}`;
    return `${label} recording`;
  }
  return noteOneLiner(note);
}

// Note-count cell: hovering the count previews the last 2 notes (Date : Note).
// The popover is portaled + fixed-positioned so the table's horizontal scroll
// container can't clip it. Click still copies the latest note to a toast.
// Memoized leads-table row. Selecting one checkbox flips `isSelected` on just
// that row; the other rows get identical props and React.memo skips them — so a
// checkbox click no longer re-renders (and momentarily reflows) the whole table.
// Column registry — one source of truth driving the header, the row cells, and
// the per-user column picker, so showing/hiding a column and its sortable header
// always stay in sync. `sort` is the frontend sort key (mapped to the real DB
// field by SORT_TO_API); a column without it isn't sortable. The checkbox is
// rendered separately; the name column is fixed (never hideable), and every
// other column can be toggled off per user.
const LEAD_COLUMNS = [
  {
    key: "name", label: "Lead / Client Name", sort: "leadName", fixed: true,
    render: (lead, ctx) => (
      <div className="Lms-Row-Name">
        {hasActiveChecklist(lead.actionItem) && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--red-500)", marginRight: 6, flexShrink: 0 }} title="Active Checklist Items" />}
        <a
          className="Lms-Lead-Open"
          href={leadDetailPath(lead.id)}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;
            event.preventDefault();
            ctx.onOpen(lead.id);
          }}
          title="Open lead (Ctrl/Cmd-click for a new tab)"
        >
          <strong>{lead.leadName}</strong>
        </a>
        {lead.important && <span title="Marked Important" style={{ marginLeft: 6, fontSize: 13, flexShrink: 0 }}>⭐</span>}
        {/* Archived leads only ever reach this table through a search, and they
            must never be mistaken for live pipeline. Quiet by design. */}
        {lead.isArchived && (
          <span
            title="This lead is archived — it is not in the live pipeline"
            style={{
              marginLeft: 6, flexShrink: 0, fontSize: 10, fontWeight: 700,
              letterSpacing: 0.4, textTransform: "uppercase",
              padding: "1px 6px", borderRadius: 999,
              border: "1px solid var(--c-300, #cbd5e1)",
              color: "var(--c-500, #64748b)", opacity: 0.85,
            }}
          >
            Archived
          </span>
        )}
      </div>
    ),
  },
  { key: "product", label: "Product", render: (lead) => <ProductBadge product={lead.product} /> },
  { key: "channel", label: "Channel", render: (lead) => <ChannelIcon channel={lead.channel} campaign={lead.campaignName} /> },
  { key: "stage", label: "Workflow Status", render: (lead) => <span className={`lms-stage-chip ${stageTone(lead.stageLabel)}`}>{lead.stageLabel}</span> },
  { key: "notesCount", label: "Lead Note Count", sort: "notesCount", render: (lead, ctx) => <NoteCountCell lead={lead} onToast={ctx.onToast} /> },
  { key: "company", label: "Company Name", sort: "companyName", render: (lead) => lead.companyName || "-" },
  { key: "email", label: "Email", render: (lead) => lead.email || "-" },
  { key: "phone", label: "Phone Nos", render: (lead) => lead.phone || "-" },
  { key: "owner", label: "Assigned To", render: (lead) => lead.ownerName || "-" },
  {
    key: "nextFollowUp", label: "Next Follow Up", sort: "nextFollowUp",
    // A missing date is as much a miss as a passed one (it's what the "Missed /
    // Previous / Not Set" tab counts), so the dash goes red too — except on a
    // closed lead, where there is nothing left to follow up.
    render: (lead) => {
      const notSet = !lead.nextFollowUpAt && !/^closed/i.test(lead.stageLabel || "");
      const red = notSet || isOverdue(lead.nextFollowUpAt);
      return (
        <span
          style={{ fontSize: 12, whiteSpace: "nowrap", color: red ? "var(--red-600)" : "var(--c-500)" }}
          title={lead.nextFollowUpAt ? formatDate(lead.nextFollowUpAt) : notSet ? "No follow-up date set" : undefined}
        >
          {friendlyDay(lead.nextFollowUpAt)}
        </span>
      );
    },
  },
  {
    key: "createdAt", label: "Created", sort: "createdAt",
    render: (lead) => <span style={{ fontSize: 12, whiteSpace: "nowrap", color: "var(--c-500)" }} title={lead.createdAt ? formatDate(lead.createdAt) : undefined}>{lead.createdAt ? friendlyDay(lead.createdAt) : "-"}</span>,
  },
  { key: "estimatedValue", label: "Est. Value", sort: "estimatedValue", render: (lead) => (Number(lead.estimatedValue || 0) > 0 ? `₹${Number(lead.estimatedValue).toLocaleString()}` : "-") },
  { key: "quality", label: "Quality", render: (lead) => { const score = computeQualityScore(lead); return <span className={`Lms-Quality-Badge ${qualityTone(score)}`}>{score}</span>; } },
  { key: "updatedAt", label: "Last Updated", sort: "updatedAt", render: (lead) => <span style={{ whiteSpace: "nowrap" }} title={formatDate(lead.updatedAt)}>{timeAgo(lead.updatedAt)}</span> },
];

// Toggleable = everything except the fixed name column. "Created" starts hidden
// so the default view matches what people see today; anyone can add it (and drop
// Est. Value / Quality) from the picker.
const TOGGLEABLE_LEAD_COLUMNS = LEAD_COLUMNS.filter((c) => !c.fixed);
const DEFAULT_VISIBLE_LEAD_COLUMNS = LEAD_COLUMNS.filter((c) => c.key !== "createdAt").map((c) => c.key);

const LeadTableRow = React.memo(function LeadTableRow({ lead, columns, isSelected, onToggle, onContextMenu, onOpen, onToast }) {
  const ctx = { onOpen, onToast };
  return (
    <tr className={lead.important ? "Lms-Row-Important" : undefined} onContextMenu={(event) => onContextMenu(event, lead.id)}>
      <td><input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); onToggle(lead.id); }} onClick={(e) => e.stopPropagation()} aria-label={`Select ${lead.leadName}`} /></td>
      {columns.map((col) => <td key={col.key}>{col.render(lead, ctx)}</td>)}
    </tr>
  );
});

function NoteCountCell({ lead, onToast }) {
  const [pop, setPop] = useState(null);
  const notes = lead.recentNotes || [];
  if (!lead.notesCount) return <span className="Lms-Muted">–</span>;
  const show = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPop({ x: rect.left + rect.width / 2, y: rect.top });
  };
  return (
    <span
      className="Lms-Note-Count"
      onMouseEnter={show}
      onMouseLeave={() => setPop(null)}
      onClick={() => { const latest = notes[0]; if (latest) onToast(noteContent(latest)); }}
    >
      {lead.notesCount}
      {pop && notes.length > 0 && createPortal(
        <div className="Lms-Note-Pop" style={{ left: pop.x, top: pop.y }}>
          {notes.map((note, index) => (
            <div key={index} className="Lms-Note-Pop-Row">
              <span className="Lms-Note-Pop-Date">{formatDateTime(note.created_at)}</span>
              <span className="Lms-Note-Pop-Sep">:</span>
              <span className="Lms-Note-Pop-Text">{hoverOneLiner(note) || "—"}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

// Inline click-to-edit control for the About panel. Reads as plain text; on click
// becomes a select (fixed options) or text input; commits on change/blur.
function EditableField({ value, options, placeholder, ariaLabel, onSave }) {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value || "");
  useEffect(() => { setTemp(value || ""); }, [value]);
  const commit = (next) => {
    setEditing(false);
    if ((next ?? "") !== (value ?? "")) onSave(next ?? "");
  };
  if (editing && options) {
    return (
      <select
        className="Lms-Edit-Control" autoFocus value={temp} aria-label={ariaLabel}
        onChange={(event) => commit(event.target.value)}
        onBlur={() => setEditing(false)}
      >
        <option value="">— Not set —</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }
  if (editing) {
    return (
      <input
        className="Lms-Edit-Control" autoFocus value={temp} placeholder={placeholder} aria-label={ariaLabel}
        onChange={(event) => setTemp(event.target.value)}
        onBlur={() => commit(temp.trim())}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.target.blur();
          if (event.key === "Escape") { setTemp(value || ""); setEditing(false); }
        }}
      />
    );
  }
  return (
    <button
      type="button" className={`Lms-Edit-Value${value ? "" : " is-empty"}`}
      onClick={() => setEditing(true)} title="Click to edit"
    >
      {value || placeholder || "—"}
    </button>
  );
}

// Minimal popup calendar for "Next Follow Up". Replaces the native date input
// because its picker always renders the full month — weeks that contain no
// selectable day (fully in the past) must not be shown at all.
function InlineDatePicker({ value, min, onSelect, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const parse = (s) => { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const selected = parse(value);
  const minDate = parse(min) || new Date();
  const [view, setView] = useState(() => { const base = selected || minDate; return new Date(base.getFullYear(), base.getMonth(), 1); });
  useEffect(() => {
    if (!open) return;
    const base = parse(value) || parse(min) || new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDocClick); document.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const weeks = [];
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const gridStart = new Date(first);
  gridStart.setDate(1 - first.getDay());
  for (let w = 0; w < 6; w += 1) {
    const days = [];
    for (let d = 0; d < 7; d += 1) { const day = new Date(gridStart); day.setDate(gridStart.getDate() + w * 7 + d); days.push(day); }
    if (days.every((day) => day.getMonth() !== view.getMonth())) continue; // padding week
    if (min && days.every((day) => fmt(day) < min)) continue;              // nothing selectable
    weeks.push(days);
  }
  const monthLabel = view.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const canPrev = !min || view.getFullYear() > minDate.getFullYear() || (view.getFullYear() === minDate.getFullYear() && view.getMonth() > minDate.getMonth());

  return (
    <span className="Lms-DatePick" ref={wrapRef}>
      <button type="button" className={`Lms-DatePick-Btn${value ? "" : " is-empty"}`} onClick={() => setOpen((v) => !v)} aria-label={ariaLabel} title="Click to pick a date">
        <Calendar size={14} />
        {value ? formatDate(value) : "Set date"}
      </button>
      {open && (
        <div className="Lms-DatePick-Pop" role="dialog" aria-label="Choose date">
          <div className="Lms-DatePick-Head">
            <button type="button" disabled={!canPrev} onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} aria-label="Previous month">‹</button>
            <strong>{monthLabel}</strong>
            <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} aria-label="Next month">›</button>
          </div>
          <div className="Lms-DatePick-Grid">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d} className="Lms-DatePick-Dow">{d}</span>)}
            {weeks.flat().map((day) => {
              const ds = fmt(day);
              const disabled = Boolean(min && ds < min);
              const outside = day.getMonth() !== view.getMonth();
              return (
                <button
                  key={ds} type="button" disabled={disabled}
                  className={`Lms-DatePick-Day${value === ds ? " sel" : ""}${outside ? " out" : ""}${ds === min ? " today" : ""}`}
                  onClick={() => { onSelect(ds); setOpen(false); }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="Lms-DatePick-Foot">
            <button type="button" onClick={() => { onSelect(""); setOpen(false); }}>Clear</button>
            <button type="button" onClick={() => { onSelect(min || fmt(new Date())); setOpen(false); }}>Today</button>
          </div>
        </div>
      )}
    </span>
  );
}

function routeLeadId(route = "") {
  const path = String(route || "").split("?")[0];
  const match = path.match(/^\/lms\/([^/]+)\/?$/);
  return match ? match[1] : "";
}

function routePage(route = "") {
  const query = String(route || "").split("?")[1] || "";
  const value = Number(new URLSearchParams(query).get("page") || 1);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
}

// URL-driven filters (used by the Analytics page drill-down links). Filters that
// already have UI here (stage / owner / source) sync into their dropdowns; the
// rest (lead_type, created_after) are URL-only and passed straight to the API.
function routeFilters(route = "") {
  const query = String(route || "").split("?")[1] || "";
  const p = new URLSearchParams(query);
  return {
    stage: p.get("stage") || "",
    source: p.get("source") || "",
    product: p.get("product") || "",
    channel: p.get("channel") || "",
    owner: p.get("owner") || "",
    leadType: p.get("lead_type") || "",
    createdAfter: p.get("created_after") || "",
    overdue: p.get("overdue") || "",
  };
}

function leadDetailPath(leadId) {
  return `/lms/${leadId}/`;
}

function getTodayDateString() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) < getTodayDateString();
}

function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function dateTimeInputFromNow(minutesFromNow = 60, durationMinutes = 30) {
  const start = new Date(Date.now() + minutesFromNow * 60000);
  start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
  const end = new Date(start.getTime() + durationMinutes * 60000);
  return { startsAt: toDateTimeInput(start), endsAt: toDateTimeInput(end) };
}

function hasActiveChecklist(actionItem) {
  if (!actionItem) return false;
  return parseChecklist(actionItem).some((item) => !item.done);
}

function computeQualityScore(lead) {
  let score = 0;
  if (lead.email && lead.email !== "-") score += 10;
  if (lead.phone && lead.phone !== "-") score += 10;
  if (lead.websiteUrl || lead.url) score += 10;
  if (Number(lead.estimatedValue || 0) > 0) score += 10;
  if (Number(lead.notesCount || 0) > 0) score += 10;
  if (lead.nextFollowUpAt) score += 10;
  if (lead.important || lead.is_important) score += 10;
  const rawStage = String(lead.stageLabel || lead.stage || "").toLowerCase();
  if (rawStage && rawStage !== "New Lead" && rawStage !== "new") score += 10;
  if (lead.actionItem && String(lead.actionItem).trim()) score += 10;
  return Math.min(100, Math.max(0, score));
}

function qualityTone(score) {
  if (score >= 70) return "green";
  if (score >= 40) return "amber";
  return "red";
}

// Compact radial score that replaces the lead avatar in the header: the ring
// fills to the lead-quality score, the number sits inside, hover shows details.
function LeadQualityRing({ score, tone, title }) {
  const size = 72;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const center = size / 2;
  return (
    <div className={`Lms-Quality-Ring ${tone}`} title={title} aria-label={title} role="img">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="Lms-Quality-Track" cx={center} cy={center} r={radius} strokeWidth={stroke} fill="none" />
        <circle
          className="Lms-Quality-Arc" cx={center} cy={center} r={radius} strokeWidth={stroke} fill="none"
          strokeDasharray={circumference} strokeDashoffset={circumference * (1 - pct / 100)}
          strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <span className="Lms-Quality-Val">{Math.round(pct)}</span>
    </div>
  );
}

function LeadRowContextMenu({ menu, onOpenTab, onOpenWindow, onCopyLink }) {
  if (!menu) return null;

  return createPortal(
    <div
      className="Lms-Lead-Context-Menu"
      style={{ left: menu.x, top: menu.y }}
      role="menu"
      aria-label="Lead Row Actions"
      onMouseDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <button type="button" role="menuitem" onClick={onOpenTab}>Open In New Tab</button>
      <button type="button" role="menuitem" onClick={onOpenWindow}>Open In New Window</button>
      <button type="button" role="menuitem" onClick={onCopyLink}>Copy Link</button>
    </div>,
    document.body
  );
}

function currentEmployeeId(data) {
  return String(data.me?.employees?.[0]?.id || (data.employees || []).find((item) => String(item.user) === String(data.me?.user?.id))?.id || "");
}

export function LmsScreen({ data, reload, navigate, route }) {
  const activeLeadId = routeLeadId(route);
  const isDetailView = Boolean(activeLeadId);

  // Data loading happens in the refresh() effect below (LEAD_LIST_KEYS /
  // LEAD_DETAIL_KEYS); the table rows themselves come from the server-paginated
  // fetchLeads() call. A second mount effect used to duplicate the lmsLeads +
  // leadTags fetches on every mount.

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [priority, setPriority] = useState("all");
  const [owner, setOwner] = useState(() => { const o = routeFilters(route).owner; return o && !/^(unassigned|none|null)$/i.test(o) ? o : "all"; });
  // null = no filter (every stage) — the historical default. A real Set means
  // "only these stages", built either from a deep link (Analytics drill-down)
  // or by checking boxes in the stage dropdown.
  const [stage, setStage] = useState(() => {
    const v = routeFilters(route).stage;
    return v ? new Set(v.split(",").map((s) => s.trim()).filter(Boolean)) : null;
  });
  const [source, setSource] = useState(() => { const s = routeFilters(route).source; return s ? normalizeLeadSource(s) : "all"; });
  const [productFilter, setProductFilter] = useState(() => {
    const v = routeFilters(route).product;
    return v ? new Set(v.split(",").map((s) => s.trim()).filter(Boolean)) : null;
  });
  const [channelFilter, setChannelFilter] = useState(() => routeFilters(route).channel || "all");
  const [moreFilter, setMoreFilter] = useState("all");
  const [sortField, setSortField] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Per-user column visibility, remembered on this device keyed by the user id,
  // so each person keeps their own view (e.g. drop Est. Value / Quality, add
  // Created). Falls back to the default set for a first-time or private session.
  const meColKey = String(data?.me?.id ?? data?.me?.username ?? "anon");
  const COLS_STORAGE_KEY = `lms.leadColumns.v1.${meColKey}`;
  const [visibleColKeys, setVisibleColKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(COLS_STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length) return new Set(arr);
    } catch { /* private mode / blocked storage — use defaults */ }
    return new Set(DEFAULT_VISIBLE_LEAD_COLUMNS);
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef(null);
  // Search is an icon that opens a small field, not a permanent pill: the pill
  // plus five tabs plus the expanded sidebar didn't fit one line, and search
  // dropped to a second row (2026-10-08). Typing still searches live; Enter,
  // Escape or a click elsewhere just tucks the field away.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDoc = (e) => { if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [searchOpen]);
  useEffect(() => {
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify([...visibleColKeys])); } catch { /* ignore */ }
  }, [visibleColKeys, COLS_STORAGE_KEY]);
  useEffect(() => {
    if (!colMenuOpen) return undefined;
    const onDoc = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [colMenuOpen]);
  const visibleColumns = LEAD_COLUMNS.filter((c) => c.fixed || visibleColKeys.has(c.key));
  const toggleColumn = (key) => setVisibleColKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const [page, setPage] = useState(() => routePage(route));
  const [pageSize, setPageSize] = useState(20);
  const [pageInput, setPageInput] = useState(String(routePage(route)));
  const [selected, setSelected] = useState(new Set());

  const [assignOpen, setAssignOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  // Deep link: /lms?new=1 opens the Add Lead form directly (shareable/bookmarkable).
  useEffect(() => {
    if (!isDetailView && new URLSearchParams(window.location.search).get("new") === "1") setAddLeadOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [slideLeadId, setSlideLeadId] = useState(null);
  const [serverResponse, setServerResponse] = useState(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [detailLoadError, setDetailLoadError] = useState("");

  // Filter To Only Business Analysts For Assignment Dropdowns. A non-superadmin
  // sees only themselves — they can't assign to or filter by other people; only a
  // super admin sees the full assignable-owner list.
  const baOptions = useMemo(() => {
    const list = serverResponse?.bas || data.lmsLeads?.bas || [];
    const full = list.length > 0 ? list : (data.employees || []);
    if (data.me?.user?.isSuperuser) return full;
    const meId = currentEmployeeId(data);
    const mine = full.filter((b) => String(b.id) === meId);
    if (mine.length) return mine;
    const meEmp = (data.employees || []).find((e) => String(e.id) === meId);
    if (meEmp) return [{ id: meId, display_name: meEmp.display_name || meEmp.name || "Me" }];
    return full; // fail-open only if we genuinely can't identify the current user
  }, [serverResponse, data.lmsLeads, data.employees, data.me]);
  const toggleLeadSelection = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [leadContextMenu, setLeadContextMenu] = useState(null);
  const [contextToast, setContextToast] = useState("");
  const [slideLeadChecklist, setSlideLeadChecklist] = useState(null);
  const [pipelineTab, setPipelineTab] = useState("active");
  const [tabCounts, setTabCounts] = useState({ active: null, updated_today: null, missed: null, new_leads: null });

  const refresh = useCallback(() => {
    reload(isDetailView ? LEAD_DETAIL_KEYS : LEAD_LIST_KEYS);
    if (isDetailView && activeLeadId) {
      fetchExternalLead();
    }
  }, [reload, isDetailView, activeLeadId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const nextPage = routePage(route);
    setPage(nextPage);
    setPageInput(String(nextPage));
  }, [route]);

  const urlFilters = useMemo(() => routeFilters(route), [route]);

  // Keep the existing dropdowns' active state in sync when arriving via a filtered URL.
  useEffect(() => {
    if (isDetailView) return;
    if (urlFilters.stage) setStage(new Set(urlFilters.stage.split(",").map((s) => s.trim()).filter(Boolean)));
    if (urlFilters.owner && !/^(unassigned|none|null)$/i.test(urlFilters.owner)) setOwner(urlFilters.owner);
    if (urlFilters.source) setSource(normalizeLeadSource(urlFilters.source));
    if (urlFilters.product) setProductFilter(new Set(urlFilters.product.split(",").map((s) => s.trim()).filter(Boolean)));
    if (urlFilters.channel) setChannelFilter(urlFilters.channel);
    if (urlFilters.overdue === "true") setMoreFilter("overdue");
  }, [route, isDetailView]);

  // A non-admin's own pipeline should be the DEFAULT view, not something they
  // have to go find in a dropdown — "By Assigned To" starts on their own name.
  // Runs once identity is known; a URL-supplied owner (analytics drill-down
  // links) still wins, and a superuser's default stays "all" unchanged.
  const [ownerDefaulted, setOwnerDefaulted] = useState(false);
  useEffect(() => {
    if (ownerDefaulted || isDetailView || !data.me) return;
    if (!urlFilters.owner && !data.me?.user?.isSuperuser) {
      const meId = currentEmployeeId(data);
      if (meId) setOwner(meId);
    }
    setOwnerDefaulted(true);
  }, [data.me, ownerDefaulted, isDetailView, urlFilters.owner]);

  const SORT_TO_API = {
    leadName: "company_name",
    companyName: "company_name",
    notesCount: "notes_count",
    createdAt: "created_at",
    updatedAt: "updated_at",
    nextFollowUp: "next_follow_up_at",
    estimatedValue: "estimated_value",
  };

  // Fast filter, search or page changes can be answered out of order; only the newest request may land.
  const leadsGate = useMemo(createLatestGate, []);
  const fetchLeads = useCallback(async () => {
    if (isDetailView) return;
    const ticket = leadsGate.start();
    setServerLoading(true);
    try {
      const params = new URLSearchParams();
      const safePg = Math.max(1, page);
      params.set("limit", String(pageSize));
      params.set("offset", String((safePg - 1) * pageSize));
      if (owner !== "all") params.set("owner", owner);
      else if (urlFilters.owner) params.set("owner", urlFilters.owner); // "unassigned" has no dropdown option — URL-only
      if (stage) params.set("stage", [...stage].join(","));
      if (source !== "all") {
        const rawSource = sourceToRaw.current?.[source] || source;
        params.set("source", rawSource);
      }
      if (productFilter) params.set("product", [...productFilter].join(","));
      if (channelFilter !== "all") params.set("channel", channelFilter);
      if (urlFilters.leadType) params.set("lead_type", urlFilters.leadType);
      if (urlFilters.createdAfter) params.set("created_after", urlFilters.createdAfter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (priority !== "all") {
        if (priority === "important") params.set("important", "true");
        else if (priority === "not_important") params.set("important", "false");
      }
      if (moreFilter !== "all") {
        if (moreFilter === "important") params.set("important", "true");
        else if (moreFilter === "scheduled") params.set("fu_set", "true");
        else if (moreFilter === "missing") params.set("fu_set", "false");
        else if (moreFilter === "overdue") params.set("overdue", "true");
        else if (moreFilter === "action") params.set("action_pending", "true");
      }
      // Pipeline Tab Filters
      if (pipelineTab === "updated_today") {
        params.set("updated_today", "true");
      } else if (pipelineTab === "missed") {
        // Date passed OR never set (the strict `overdue` filter is a separate
        // thing — the homepage link and the "Overdue Follow Up" dropdown).
        params.set("missed", "true");
      } else if (pipelineTab === "new_leads") {
        params.set("stage", "New Lead");
      } else if (pipelineTab === "active" && !stage && !debouncedSearch) {
        // Active Pipeline shows open leads only — closed leads stay reachable via the stage chips.
        //
        // Suspended while a search is running. Active Pipeline is the tab you
        // land on, so this exclusion was silently applied to every search
        // anybody ever typed: look up a lead that has been closed won, lost or
        // cold and the CRM answered "no such lead". Searching a name is a
        // question about the whole database, not about the tab you happen to
        // be standing on.
        params.set("exclude_stage", "Closed - Won,Closed - Lost,Closed - Cold");
      }
      const apiField = SORT_TO_API[sortField] || "created_at";
      params.set("sort_by", sortDirection === "desc" ? `-${apiField}` : apiField);
      const result = await apiGet(`/Lms/api/leads/?${params.toString()}`);
      if (!leadsGate.isCurrent(ticket)) return;
      setServerResponse(result);
      if (result?.tab_counts) {
        setTabCounts({
          active: result.tab_counts.active ?? null,
          updated_today: result.tab_counts.updated_today ?? null,
          missed: result.tab_counts.missed ?? null,
          new_leads: result.tab_counts.new_leads ?? null,
          all: result.tab_counts.all ?? null,
        });
      }
    } catch {
      if (!leadsGate.isCurrent(ticket)) return;
      setError("Failed To Load Leads.");
      setTimeout(() => setError(""), 3000);
    } finally {
      if (leadsGate.isCurrent(ticket)) setServerLoading(false);
    }
  }, [leadsGate, page, pageSize, owner, stage, source, productFilter, channelFilter, debouncedSearch, moreFilter, sortField, sortDirection, isDetailView, data.leadTags, pipelineTab, urlFilters]);

  const originCountsData = serverResponse?.origin_counts || data.lmsLeads?.origin_counts || {};
  const sourceToRaw = useRef({});
  useMemo(() => {
    const rawInitial = data.lmsLeads?.origin_counts || {};
    const rawServer = serverResponse?.origin_counts || {};
    const allKeys = Array.from(new Set([...Object.keys(rawInitial), ...Object.keys(rawServer)]));
    
    const mapping = {};
    allKeys.forEach((k) => {
      const label = normalizeLeadSource(k);
      if (!mapping[label]) {
        mapping[label] = [];
      }
      if (!mapping[label].includes(k)) {
        mapping[label].push(k);
      }
    });
    
    sourceToRaw.current = Object.fromEntries(
        Object.entries(mapping).map(([label, keys]) => [label, keys.join(",")])
    );
  }, [data.lmsLeads, serverResponse]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const leads = useMemo(() => {
    // The open lead is served by the per-lead call (externalLead), not by this list.
    if (isDetailView) return [];
    if (!serverResponse?.results) return [];
    return serverResponse.results.map((row) => ({
      id: row.id,
      leadName: row.full_name || row.company_name || "-",
      companyName: row.company_name || "-",
      sourceLabel: normalizeLeadSource(row.source || ""),
      product: row.product || "",
      channel: row.attr_source || "",
      campaignName: row.campaign_name || "",
      stageLabel: row.stage || "New Lead",
      notesCount: row.notes_count ?? 0,
      recentNotes: row.recent_notes || [],
      ownerId: row.owner_id ? String(row.owner_id) : "",
      ownerName: row.owner_name || "-",
      priority: row.priority || "Normal",
      estimatedValue: Number(row.estimated_value || 0),
      upfrontValue: Number(row.upfront_value || 0),
      dealSize: Number(row.deal_size || 0),
      email: row.email || "-",
      phone: row.phone || "-",
      url: row.url || "",
      websiteUrl: row.website_url || "",
      leadType: row.lead_type || "",
      important: Boolean(row.is_important),
      // Only ever true on a search result — archived leads are kept out of
      // every browse view. The row must say so, or an archived record reads
      // as live pipeline.
      isArchived: Boolean(row.is_archived),
      message: row.message || "",
      createdAt: row.created_at || null,
      updatedAt: row.updated_at || null,
      nextFollowUpAt: row.next_follow_up_at || null,
      lastUpdateNote: row.latest_comment || "-",
      industry: row.industry || "-",
      tagNames: (row.tags || []).map((t) => t.name),
      actionItem: row.action_item || "",
      tagIds: (row.tags || []).map((t) => String(t.id)),
      tags: row.tags || [],
    }));
  }, [isDetailView, serverResponse]);

  useEffect(() => {
    if (slideLeadId) {
      const slideLead = leads.find((l) => String(l.id) === String(slideLeadId));
      if (slideLead) {
        setSlideLeadChecklist(parseChecklist(slideLead.actionItem));
      }
    } else {
      setSlideLeadChecklist(null);
    }
  }, [slideLeadId, leads]);

  // Runs one request per selected lead. Leads that worked are done; any that failed stay selected
  // so a retry touches only them, and the person is told how it went.
  const bulkRun = async (verb, run, onAllDone) => {
    const ids = Array.from(selected);
    setBusy(true);
    try {
      const { failed, firstReason } = await runForEach(ids, run);
      refresh();
      if (!failed.length) {
        setSelected(new Set());
        onAllDone();
        return;
      }
      setSelected(new Set(failed));
      onAllDone();
      setError(bulkOutcomeMessage(ids.length, failed.length, errorText(firstReason, ""), verb));
      setTimeout(() => setError(""), 8000);
    } finally {
      setBusy(false);
    }
  };

  const assignSelected = async (employeeId) => {
    if (!employeeId || !selected.size) return;
    await bulkRun("assigned", (leadId) => apiPatch(`/Banao/LeadAccounts/${leadId}/`, { owner: employeeId }), () => setAssignOpen(false));
  };

  const bulkMoveStage = async (toStage) => {
    if (!toStage || !selected.size) return;
    await bulkRun("moved", (leadId) => apiPost(`/Banao/LeadAccounts/${leadId}/move-stage/`, { to_stage: toStage, reason: "Bulk Update" }), () => setMoreOpen(false));
  };

  const addLead = async (formData) => {
    if (!formData.company_name?.trim()) {
      setError("Company Name Is Required"); setTimeout(() => setError(""), 3000);
      return;
    }
    if (!formData.contact_email?.trim() && !formData.contact_phone?.trim()) {
      setError("At Least Email Or Phone Number Is Required."); setTimeout(() => setError(""), 3000);
      return;
    }
    setBusy(true);
    try {
      const newLead = await apiPost("/Banao/LeadAccounts/capture/", formData);
      refresh();
      setAddLeadOpen(false);
      if (newLead?.id) {
        navigate(`/lms/${newLead.id}/`);
      }
    } catch (err) {
      let msg = "Unable To Create Lead.";
      const errorData = err?.payload || err?.data;
      if (errorData) {
        if (typeof errorData === "string") {
          msg = errorData;
        } else if (errorData.detail) {
          msg = errorData.detail;
        } else if (errorData.message) {
          msg = errorData.message;
        } else if (typeof errorData === "object") {
          msg = Object.entries(errorData)
            .map(([field, msgs]) => {
              const fieldName = field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
              return `${fieldName}: ${Array.isArray(msgs) ? msgs.join(" ") : msgs}`;
            })
            .join(" | ");
        }
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
      setTimeout(() => setError(""), 5000);
    } finally {
      setBusy(false);
    }
  };

  const filtered = leads;
  const totalCount = isDetailView ? filtered.length : (serverResponse?.count ?? filtered.length);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = filtered.length ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pagedLeads = isDetailView ? filtered.slice(pageStart, pageEnd) : filtered;
  const selectedVisibleCount = pagedLeads.filter((lead) => selected.has(lead.id)).length;
  const allVisibleSelected = pagedLeads.length > 0 && pagedLeads.every((lead) => selected.has(lead.id));
  const activeLead = activeLeadId ? leads.find((lead) => String(lead.id) === String(activeLeadId)) : null;

  const sourceCards = useMemo(() => {
    const raw = serverResponse?.origin_counts || data.lmsLeads?.origin_counts || {};
    
    const countsByNormalizedLabel = {};
    Object.entries(raw).forEach(([sourceKey, count]) => {
      const label = normalizeLeadSource(sourceKey);
      countsByNormalizedLabel[label] = (countsByNormalizedLabel[label] || 0) + Number(count || 0);
    });

    const labels = [
      { label: "Banao Website", tone: "banao" },
      { label: "Instagram", tone: "instagram" },
      { label: "LinkedIn", tone: "linkedin" },
      { label: "InterviewGod", tone: "interviewgod" },
      { label: "Vikaas", tone: "vikaas" },
    ];

    const cards = labels.map(({ label, tone }) => ({
      label,
      count: countsByNormalizedLabel[label] || 0,
      tone,
    }));

    let otherCount = 0;
    const predefinedSet = new Set(labels.map((l) => l.label));
    Object.entries(countsByNormalizedLabel).forEach(([label, count]) => {
      if (!predefinedSet.has(label)) {
        otherCount += count;
      }
    });

    if (otherCount > 0) {
      cards.push({ label: "Other", count: otherCount, tone: "dynamic" });
    }

    return cards.filter((c) => c.count > 0 || c.label !== "Other" || source === c.label);
  }, [serverResponse, data.lmsLeads, source]);

  const stageOptions = useMemo(() => uniqueOptions([
    // Canonical Pipeline Order — Must Match Backend STAGE_MAP (migrate_legacy_banao_leads.py)
    "New Lead", "Contact Attempted", "Engaged",
    "Discovery / Demo Scheduled", "Demo Done / Solution Fit",
    "Proposal Sent", "Closed - Won", "Closed - Lost", "Closed - Cold", "Nurture / Recycle",
    // Also Include Any Stage Currently Present On Loaded Leads (Catches custom/future Stages)
    ...leads.map((lead) => lead.stageLabel),
  ]), [leads]);

  const goToPage = (value) => {
    const number = Math.max(1, Math.min(totalPages, Math.floor(Number(value) || 1)));
    setPage(number);
    setPageInput(String(number));
    navigate?.(`/lms/?page=${number}`);
  };

  useEffect(() => {
    setPage(1);
    setPageInput("1");
  }, [debouncedSearch, priority, owner, stage, source, productFilter, channelFilter, moreFilter, sortField, sortDirection, pageSize, pipelineTab]);

  useEffect(() => {
    if (!isDetailView && page > totalPages) goToPage(totalPages);
  }, [page, totalPages, isDetailView]);

  const resetFilters = () => {
    setSearch("");
    setPriority("all");
    setOwner("all");
    setStage(null);
    setSource("all");
    setProductFilter(null);
    setChannelFilter("all");
    setMoreFilter("all");
    setSelected(new Set());
    setSortField("createdAt");
    setSortDirection("desc");
    setPipelineTab("active");
    goToPage(1);
    refresh();
  };

  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        pagedLeads.forEach((lead) => next.delete(lead.id));
      } else {
        pagedLeads.forEach((lead) => next.add(lead.id));
      }
      return next;
    });
  }, [allVisibleSelected, pagedLeads]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection(field === "leadName" ? "asc" : "desc");
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />;
  };

  const closeLeadContextMenu = useCallback(() => {
    setLeadContextMenu(null);
  }, []);

  const handleLeadContextMenu = useCallback((event, leadId) => {
    event.preventDefault();
    setLeadContextMenu({ x: event.pageX, y: event.pageY, leadId });
  }, []);

  // Stable so memoized rows don't re-render when the parent re-renders.
  const openLeadRow = useCallback((leadId) => navigate(leadDetailPath(leadId)), [navigate]);

  const openLeadFromContext = useCallback((features) => {
    if (!leadContextMenu?.leadId) return;
    const url = leadDetailPath(leadContextMenu.leadId);
    if (features) {
      window.open(url, "_blank", features);
    } else {
      window.open(url, "_blank");
    }
    closeLeadContextMenu();
  }, [closeLeadContextMenu, leadContextMenu]);

  const copyLeadLinkFromContext = useCallback(async () => {
    if (!leadContextMenu?.leadId) return;
    try {
      await navigator.clipboard.writeText(leadDetailPath(leadContextMenu.leadId));
      setContextToast("Link Copied!");
    } catch {
      setError("Unable To Copy Link.");
      window.setTimeout(() => setError(""), 3000);
    } finally {
      closeLeadContextMenu();
    }
  }, [closeLeadContextMenu, leadContextMenu]);

  useEffect(() => {
    if (!leadContextMenu) return undefined;

    const handleMouseDown = () => closeLeadContextMenu();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeLeadContextMenu();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeLeadContextMenu, leadContextMenu]);

  useEffect(() => {
    if (!contextToast) return undefined;
    const timer = window.setTimeout(() => setContextToast(""), 1800);
    return () => window.clearTimeout(timer);
  }, [contextToast]);

  const [externalLead, setExternalLead] = useState(null);
  
  const fetchExternalLead = useCallback(async () => {
    if (!isDetailView || !activeLeadId) return;
    setDetailLoadError("");
    try {
      const res = await apiGet(`/Lms/lms/lead/${activeLeadId}/`);
      setExternalLead(res);
    } catch (err) {
      setExternalLead(null);
      const message = err?.data?.lead || err?.data?.detail || err?.message || "Lead Not Found";
      setDetailLoadError(message);
      setError(message);
      setTimeout(() => setError(""), 3000);
    }
  }, [isDetailView, activeLeadId]);

  if (activeLeadId && detailLoadError && (!externalLead || String(externalLead.lead_id) !== String(activeLeadId))) {
    return (
      <section className="Lms-Screen Lms-Detail-Screen">
        <div className="Lms-Breadcrumb">
          <strong>Intranet</strong>
          <button onClick={() => navigate?.("/home/")}>Home</button>
          <span>/</span>
          <button onClick={() => navigate?.("/lms/")}>Leads</button>
          <span>/</span>
          <b>{activeLeadId}</b>
        </div>
        <EmptyState title="Lead Could Not Be Loaded" description={detailLoadError} />
      </section>
    );
  }

  if (activeLeadId && !activeLead && (!externalLead || String(externalLead.lead_id) !== String(activeLeadId))) {
    return (
      <section className="Lms-Screen Lms-Detail-Screen">
        <div className="Lms-Breadcrumb">
          <strong>Intranet</strong>
          <button onClick={() => navigate?.("/home/")}>Home</button>
          <span>/</span>
          <button onClick={() => navigate?.("/lms/")}>Leads</button>
          <span>/</span>
          <b>{activeLeadId}</b>
        </div>
        <div style={{ padding: 40, textAlign: "center", color: "var(--c-500)" }}>
           Loading Lead Information...
        </div>
      </section>
    );
  }

  // If We Fetched The Lead Externally, Construct A UI-Friendly Version
  const effectiveLead = (externalLead && externalLead.lead_id && String(externalLead.lead_id) === String(activeLeadId) ? (() => {
      const contacts = externalLead.contacts || [];
      const primary = contacts.find((c) => c.is_primary) || contacts[0] || {};
      return {
        id: externalLead.lead_id,
        leadName: externalLead.full_name || externalLead.company_name || "-",
        companyName: externalLead.company_name || "-",
        sourceLabel: normalizeLeadSource(externalLead.source || ""),
        product: externalLead.product || "",
        channel: externalLead.attr_source || "",
        campaignName: externalLead.campaign_name || "",
        stageLabel: externalLead.stage || "New Lead",
        priority: externalLead.priority || "Normal",
        ownerId: String(externalLead.owner_id || ""),
        ownerName: externalLead.owner_name || "-",
        estimatedValue: Number(externalLead.estimated_value || 0),
        upfrontValue: Number(externalLead.upfront_value || 0),
        dealSize: Number(externalLead.deal_size || 0),
        currency: externalLead.currency || "",
        url: externalLead.url || "",
        leadType: externalLead.lead_type || "",
        important: Boolean(externalLead.is_important),
        message: externalLead.message || "",
        email: primary.email || externalLead.email || "-",
        phone: primary.phone || externalLead.phone || "-",
        contactVisible: externalLead.contact_visible !== false,
        websiteUrl: externalLead.website_url || "",
        createdAt: externalLead.created_at || null,
        updatedAt: externalLead.updated_at || null,
        nextFollowUpAt: externalLead.next_follow_up_at || null,
        lastUpdateNote: externalLead.latest_comment || "-",
        industry: externalLead.industry || "-",
        actionItem: externalLead.action_item || "",
        tags: externalLead.tags || [],
        tagIds: (externalLead.tags || []).map((t) => String(t.id || t)),
        tagNames: (externalLead.tags || []).map((t) => t.name || t),
        metadata: externalLead.metadata || {},
        notes: externalLead.notes || [],
        contacts,
        proposals: externalLead.proposals || [],
        audits: externalLead.audits || [],
        activities: externalLead.activities || [],
      };
  })() : null) || activeLead;

  if (effectiveLead) {
    return <LeadDetailWorkspace data={data} lead={effectiveLead} navigate={navigate} refresh={refresh} stageOptions={stageOptions} baOptions={baOptions} />;
  }

  if (activeLeadId) {
    return (
      <section className="Lms-Screen Lms-Detail-Screen">
        <div className="Lms-Breadcrumb">
          <strong>Intranet</strong>
          <button onClick={() => navigate?.("/home/")}>Home</button>
          <span>/</span>
          <button onClick={() => navigate?.("/lms/")}>Leads</button>
          <span>/</span>
          <b>{activeLeadId}</b>
        </div>
        <div style={{ padding: 40, textAlign: "center", color: "var(--c-500)" }}>
          Loading Lead Information...
        </div>
      </section>
    );
  }

  return (
    <section className="Lms-Screen">
      <div className="Lms-Breadcrumb">
        <strong>Intranet</strong>
        <button onClick={() => navigate?.("/home/")}>Home</button>
        <span>/</span>
        <button onClick={() => navigate?.("/lms/")}>Leads</button>
        <span>/</span>
        <b>Sales CRM</b>
      </div>

      {error && <div style={{ fontSize: 13, padding: "8px 14px", marginBottom: 12, borderRadius: 6, background: "var(--red-50)", color: "var(--red-600)", border: "1px solid var(--red-200)" }}>{error}</div>}

      <section className="Lms-Leads-Panel">
        {/* One bar: pipeline tabs on the left, small search + reset on the right.
            Wraps on tablet/mobile (search goes full-width under the tabs). */}
        <div className="Lms-Pipeline-Bar">
          <div className="Lms-Pipeline-Tabs">
            {/* Two groups: the scope of the list (open work vs everything) on the
                left, then the "what needs me" slices, divided so they don't read
                as one flat row of five equals. */}
            {[
              { key: "active", label: "Active Pipeline", cls: "" },
              { key: "all", label: "All", cls: "", hint: "Every lead in this view — open and closed" },
              { key: "updated_today", label: "Updated Today", cls: "tab-updated", hint: "Leads With A Note Added Today — Closed Leads Included", dividerBefore: true },
              { key: "missed", label: "Missed / Previous / Not Set", cls: "tab-missed", hint: "Open leads whose follow-up date has passed, or was never set" },
              { key: "new_leads", label: "New Leads", cls: "tab-new" },
            ].map((tab) => (
              <React.Fragment key={tab.key}>
                {tab.dividerBefore && <span className="Lms-Pipeline-Divider" aria-hidden="true" />}
                <button
                  title={tab.hint || ""}
                  className={`Lms-Pipeline-Tab ${tab.cls} ${pipelineTab === tab.key ? "active" : ""}`}
                  onClick={() => { setPipelineTab(tab.key); }}
                >
                  {tab.label}
                  {tabCounts[tab.key] != null && <span className="tab-count">{tabCounts[tab.key]}</span>}
                </button>
              </React.Fragment>
            ))}
          </div>
          <div className="Lms-Pipeline-Tools">
            <div className="Lms-Search-Wrap" ref={searchWrapRef}>
              {/* Blue while a search is narrowing the list, so a hidden query
                  can't quietly explain "why are there so few leads". */}
              <button
                className={"Icon-Button" + (search.trim() ? " Lms-Search-On" : "")}
                title={search.trim() ? `Searching for "${search.trim()}"` : "Search"}
                aria-label="Search leads"
                aria-expanded={searchOpen}
                onClick={() => setSearchOpen((v) => !v)}
              >
                <Search size={18} />
              </button>
              {searchOpen && (
                <div className="Lms-Search-Pop">
                  <div className="Lms-Search">
                    <Search size={16} style={{ color: "var(--c-500)", flexShrink: 0 }} />
                    <input
                      autoFocus
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") { setDebouncedSearch(search); setSearchOpen(false); }
                        if (event.key === "Escape") setSearchOpen(false);
                      }}
                      placeholder="Search By Name, Email, Or Company"
                    />
                  </div>
                </div>
              )}
            </div>
            <button className="Icon-Button" title="Reset Filters" onClick={resetFilters}>
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {/* Always expanded, right under the filter row — this used to be a
            collapsed <details> sitting below the table toolbar, which hid the
            one filter (Assigned To) a non-admin actually needs to find their
            own leads. See the owner-default effect above for the other half
            of that fix: it's now preselected to you, not just reachable. */}
        <div className="Lms-Filters">
          <select value={priority} onChange={(event) => setPriority(event.target.value)}>
            <option value="all">By Mark Imp</option>
            {["Low", "Normal", "High"].map((value) => <option key={value} value={value}>{value}</option>)}
          </select>

          <select value={owner} onChange={(event) => setOwner(event.target.value)}>
            <option value="all">By Assigned To</option>
            {baOptions.map((employee) => <option key={employee.id} value={employee.id}>{employee.display_name}</option>)}
          </select>
          <CheckboxDropdown
            label="By Workflow Status"
            options={stageOptions.map((value) => ({ value, label: value }))}
            selected={stage || new Set(stageOptions)}
            onToggle={(value) => setStage((prev) => {
              const base = new Set(prev || stageOptions);
              if (base.has(value)) base.delete(value); else base.add(value);
              return base;
            })}
            onSelectAll={() => setStage(null)}
            onClear={() => setStage(new Set())}
          />
          <CheckboxDropdown
            label="By Product"
            options={PRODUCT_OPTIONS.map((value) => ({ value, label: value }))}
            selected={productFilter || new Set(PRODUCT_OPTIONS)}
            onToggle={(value) => setProductFilter((prev) => {
              const base = new Set(prev || PRODUCT_OPTIONS);
              if (base.has(value)) base.delete(value); else base.add(value);
              return base;
            })}
            onSelectAll={() => setProductFilter(null)}
            onClear={() => setProductFilter(new Set())}
          />
          <select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
            <option value="all">By Channel</option>
            {CHANNEL_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <select value={moreFilter} onChange={(event) => setMoreFilter(event.target.value)}>
            <option value="all">More Filters</option>
            <option value="important">★ Important Only</option>
            <option value="scheduled">Has Follow Up</option>
            <option value="missing">No Follow Up</option>
            <option value="overdue">Overdue Follow Up</option>
            <option value="action">Action Pending</option>
          </select>
        </div>

        <div className="Lead-Tools">
          {(canCreate(data.me, "sales_crm") || canUpdate(data.me, "sales_crm") || canDelete(data.me, "sales_crm")) && (
            <button className="Icon-Action" title="Add Lead" onClick={() => setAddLeadOpen(true)}><Plus size={16} /></button>
          )}
          <button className="Icon-Action" title="Bulk Move Stage" onClick={() => setMoreOpen(true)} disabled={!selected.size}><MoreHorizontal size={16} /></button>
          <button className="Icon-Action" title="Delete Selected" onClick={async () => {
            if (!selected.size) return;
            const ids = Array.from(selected);
            const byId = new Map(leads.map((l) => [String(l.id), l]));
            // Delete means two different things depending on where the lead
            // already sits. A live lead is archived (soft-deleted): it leaves
            // the pipeline but stays findable by search, so a re-added
            // duplicate can be spotted. A lead that is ALREADY archived has
            // served that purpose and is purged for good. Without this split,
            // hitting delete on an archived lead just re-archived it — a
            // no-op — which is why archived leads could never be removed.
            const toPurge = ids.filter((id) => byId.get(String(id))?.isArchived);
            const toArchive = ids.filter((id) => !byId.get(String(id))?.isArchived);
            let msg;
            if (toPurge.length && toArchive.length) msg = `${toArchive.length} lead(s) will be archived; ${toPurge.length} already-archived lead(s) will be permanently deleted. Continue?`;
            else if (toPurge.length) msg = `Permanently delete ${toPurge.length} archived lead(s)? This cannot be undone.`;
            else msg = `Archive ${toArchive.length} selected lead(s)? They leave the pipeline but stay findable by search.`;
            if (!window.confirm(msg)) return;
            setBusy(true);
            try {
              await Promise.all([
                ...toArchive.map((id) => apiPatch(`/Banao/LeadAccounts/${id}/`, { is_active: false })),
                ...toPurge.map((id) => apiDelete(`/Banao/LeadAccounts/${id}/`)),
              ]);
              setSelected(new Set());
              refresh();
            } catch (err) { setError(err?.data?.detail || err?.message || "Delete Failed."); setTimeout(() => setError(""), 3000); } finally { setBusy(false); }
          }} disabled={!selected.size}><Trash2 size={16} /></button>
          <button className="Icon-Action" title="Assign Selected" onClick={() => setAssignOpen(true)} disabled={!selected.size}><Users size={16} /></button>
          {/* Only when something is ticked -- "0 selected" is noise. Counts the
              whole selection (what the bulk buttons act on), not just this page. */}
          {selected.size > 0 && <span className="Lead-Selected-Count">{selected.size} selected</span>}
          {/* Per-user column picker — pushed to the right; add/remove columns and
              it's remembered for you. */}
          <div ref={colMenuRef} style={{ marginLeft: "auto", position: "relative" }}>
            <button className={colMenuOpen ? "Icon-Action Active" : "Icon-Action"} title="Choose columns" onClick={() => setColMenuOpen((v) => !v)}><SlidersHorizontal size={16} /></button>
            {colMenuOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 60, background: "var(--surface)", border: "1px solid var(--c-300, #cbd5e1)", borderRadius: 10, padding: 6, minWidth: 220, maxHeight: 360, overflowY: "auto", boxShadow: "0 10px 30px rgba(0,0,0,0.25)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--c-500)", padding: "6px 10px 8px" }}>Show columns</div>
                {TOGGLEABLE_LEAD_COLUMNS.map((col) => (
                  <label key={col.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", cursor: "pointer", fontSize: 13, borderRadius: 6 }}>
                    <input type="checkbox" checked={visibleColKeys.has(col.key)} onChange={() => toggleColumn(col.key)} />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="Lead-Table-Scroll">
          <table className="Erp-Table Lms-Lead-Table">
            <thead>
              <tr>
                <th><input type="checkbox" checked={allVisibleSelected} onChange={(e) => { e.stopPropagation(); toggleAllVisible(); }} onClick={(e) => e.stopPropagation()} aria-label="Select All Visible Leads" /></th>
                {visibleColumns.map((col) => (
                  <th key={col.key}>
                    {col.sort
                      ? <button className="Lms-Sort-Head" onClick={() => toggleSort(col.sort)}>{col.label} {renderSortIcon(col.sort)}</button>
                      : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedLeads.map((lead) => (
                <LeadTableRow
                  key={lead.id}
                  lead={lead}
                  columns={visibleColumns}
                  isSelected={selected.has(lead.id)}
                  onToggle={toggleLeadSelection}
                  onContextMenu={handleLeadContextMenu}
                  onOpen={openLeadRow}
                  onToast={setContextToast}
                />
              ))}
              {!filtered.length && !serverLoading && (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>
                    <div className="Lms-Empty-Wrap"><EmptyState label="No Leads Match The Current Filters." /></div>
                  </td>
                </tr>
              )}
              {!filtered.length && serverLoading && (
                <tr>
                  <td colSpan={visibleColumns.length + 1}>
                    <div className="Lms-Empty-Wrap"><EmptyState label="Loading Leads..." /></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="Lms-Pagination">
          <span>Showing {totalCount ? pageStart + 1 : 0}-{pageEnd} Of {totalCount}</span>
          <div className="Lms-Pagination-Controls">
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {PAGE_SIZE_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <button className="Icon-Action" title="Previous Page" onClick={() => goToPage(safePage - 1)} disabled={safePage <= 1}>
              <ChevronLeft size={16} />
            </button>
            <span className="Lms-Page-Jump">
              Page
              <input
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") goToPage(pageInput); }}
              />
              Of {totalPages}
            </span>
            <button className="Soft-Button Small" onClick={() => goToPage(pageInput)}>Go</button>
            <button className="Icon-Action" title="Next Page" onClick={() => goToPage(safePage + 1)} disabled={safePage >= totalPages}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <LeadRowContextMenu
        menu={leadContextMenu}
        onOpenTab={() => openLeadFromContext()}
        onOpenWindow={() => openLeadFromContext("noopener")}
        onCopyLink={copyLeadLinkFromContext}
      />
      {contextToast && createPortal(<div className="Lms-Context-Toast">{contextToast}</div>, document.body)}
      {addLeadOpen && <AddLeadModal data={data} bas={baOptions} sources={sourceCards} busy={busy} error={error} onClose={() => setAddLeadOpen(false)} onSubmit={addLead} />}
      {assignOpen && <AssignLeadModal bas={baOptions} count={selected.size} busy={busy} onClose={() => setAssignOpen(false)} onSubmit={assignSelected} />}
      {moreOpen && <BulkStageModal count={selected.size} busy={busy} options={stageOptions} onClose={() => setMoreOpen(false)} onSubmit={bulkMoveStage} />}
      {slideLeadId && (() => {
        const slideLead = leads.find((l) => String(l.id) === String(slideLeadId));
        if (!slideLead) return null;
        const empOptions = baOptions.map((e) => ({ id: e.id, name: e.display_name }));
        return (
          <div className="Modal-Backdrop" onClick={() => setSlideLeadId(null)}>
            <section className="Modal Wide" onClick={(e) => e.stopPropagation()} style={{ width: "min(900px, calc(100vw - 56px))" }}>
              <div className="Modal-Body" style={{ maxHeight: "80vh", overflow: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-200)" }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{slideLead.leadName}</h2>
                    <span style={{ color: "var(--c-500)", fontSize: 13 }}>{slideLead.companyName || ""} · {slideLead.stageLabel}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value="" onChange={(e) => { const val = e.target.value; if (!val) return; apiPatch(`/Banao/LeadAccounts/${slideLead.id}/`, { owner: Number(val) }).then(() => { refresh(); setSlideLeadId(null); }).catch((err) => console.error("Switch BA:", err)); }} style={{ fontSize: 12, padding: "3px 6px" }}>
                      <option value="">Switch BA</option>
                      {baOptions.map((emp) => <option key={emp.id} value={emp.id}>{emp.display_name}</option>)}
                    </select>
                    <button className="Soft-Button Small" onClick={() => { setSlideLeadId(null); navigate?.(`/lms/${slideLead.id}/`); }}>Open Full Page</button>
                    <button className="Soft-Button Small" onClick={() => setSlideLeadId(null)}>Close</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {[
                    ["Priority", slideLead.priority || "-"],
                    ["Stage", slideLead.stageLabel],
                    ["Owner", slideLead.ownerName || "-"],
                    ["Source", slideLead.sourceLabel || "-"],
                    ["Email", slideLead.email || "-"],
                    ["Phone", slideLead.phone || "-"],
                    ["Company", slideLead.companyName || "-"],
                    ["Created", formatDate(slideLead.createdAt)],
                    ["Updated", formatDate(slideLead.updatedAt)],
                    ["Next Follow-Up", slideLead.nextFollowUpAt ? formatDate(slideLead.nextFollowUpAt) : "-"],
                    ["Est. Value", Number(slideLead.estimatedValue || 0) > 0 ? `₹${Number(slideLead.estimatedValue).toLocaleString()}` : "-"],
                    ["Action Item", slideLead.actionItem ? `${slideLead.actionItem.slice(0, 50)}...` : "-"],
                    ["Website URL", slideLead.websiteUrl || "-"],
                    ["Lead URL", slideLead.url || "-"],
                  ].map(([label, value]) => (
                    <div key={label} style={{ padding: "6px 0" }}>
                      <dt style={{ fontSize: 11, color: "var(--c-400)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</dt>
                      <dd style={{ margin: 0, fontSize: 13, color: "var(--c-900)", fontWeight: 500 }}>{value}</dd>
                    </div>
                  ))}
                </div>
                {/* Notes */}
                {slideLead.notes && (
                  <div style={{ borderTop: "1px solid var(--c-200)", paddingTop: 12 }}>
                    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Notes ({slideLead.notes.length})</h4>
                    <div style={{ maxHeight: 200, overflow: "auto", marginBottom: 8 }}>
                      {slideLead.notes.slice(0, 10).map((note) => (
                        <div key={note.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--c-100)" }}>
                          <p style={{ margin: 0, color: "var(--c-800)", whiteSpace: "pre-wrap" }}>{noteContent(note)}</p>
                          <span style={{ fontSize: 11, color: "var(--c-400)" }}>{formatDateTime(note.created_at || note.updated_at)}</span>
                          {note.metadata?.next_follow_up_at && (
                            <div style={{ fontSize: 11, color: "var(--indigo-500)", marginTop: 2 }}>
                              Follow-Up: {formatDate(note.metadata.next_follow_up_at)}
                            </div>
                          )}
                        </div>
                      ))}
                      {!slideLead.notes.length && <span style={{ color: "var(--c-400)", fontSize: 13 }}>No Notes.</span>}
                    </div>
                  </div>
                )}

                {/* Action Checklist */}
                {slideLead.actionItem !== undefined && (
                  <div style={{ borderTop: "1px solid var(--c-200)", paddingTop: 12 }}>
                    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Action Items</h4>
                    {slideLead.actionItem ? (
                      <div style={{ fontSize: 13, whiteSpace: "pre-wrap", background: "var(--surface-soft)", padding: 12, borderRadius: 8, border: "1px solid var(--c-200)" }}>
                        {(slideLeadChecklist || parseChecklist(slideLead.actionItem)).map((item) => (
                           <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0" }}>
                             <input type="checkbox" checked={item.done} onChange={() => {
                               const currentList = slideLeadChecklist || parseChecklist(slideLead.actionItem);
                               const updated = currentList.map((entry) => entry.id === item.id ? { ...entry, done: !entry.done } : entry);
                               setSlideLeadChecklist(updated);
                               apiPatch(`/Banao/LeadAccounts/${slideLead.id}/`, { action_item: serializeChecklist(updated) })
                                 .then(() => {
                                   refresh();
                                 })
                                 .catch((err) => {
                                   console.error("Update Checklist Failed:", err);
                                   setSlideLeadChecklist(currentList);
                                 });
                             }} style={{ accentColor: "var(--blue-500)", cursor: "pointer", marginTop: "3px", flexShrink: 0 }} />
                             <span style={{ textDecoration: item.done ? "line-through" : "none", color: item.done ? "var(--c-400)" : "var(--c-900)", wordBreak: "break-word", overflowWrap: "break-word" }}>{item.label}</span>
                           </div>
                         ))}
                      </div>
                    ) : <span style={{ color: "var(--c-400)", fontSize: 13 }}>No Action Items.</span>}
                  </div>
                )}
              </div>
            </section>
          </div>
        );
      })()}
    </section>
  );
}

function LeadDetailWorkspace({ data, lead, navigate, refresh, stageOptions, baOptions }) {
  // CRM add-ons (plugin platform S6): a switched-off add-on's panel loads nothing and
  // shows nothing, instead of an error. Every add-on is on today.
  const addonOn = (code) => (data?.me?.plugins || {})[code] !== false;
  // Window title: "Client - Company | Sales CRM" so leads are identifiable in
  // the tab strip. RouteRenderer resets the title on navigation away.
  useEffect(() => {
    const client = (lead?.leadName || "").trim();
    const company = (lead?.companyName || "").trim();
    const label = client && company && client.toLowerCase() !== company.toLowerCase() ? `${client} - ${company}` : (client || company);
    if (label) document.title = `${label} | Sales CRM`;
  }, [lead?.leadName, lead?.companyName]);

  const [draft, setDraft] = useState(() => buildLeadDraft(lead, data.leadTags || []));
  // The form as the server last knew it (as opened, then as last saved): a save sends only the
  // fields that differ from this, so it never overwrites a colleague's change to a field untouched here.
  const savedDraft = useRef(draft);
  const [checklistInput, setChecklistInput] = useState("");
  const [note, setNote] = useState(() => ({ title: "", body: "", channelStatus: {} }));
  const [saving, setSaving] = useState(false);
  const [noteBusy, setNoteBusy] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [meetingToolsOpen, setMeetingToolsOpen] = useState(false);
  const [meetingEvents, setMeetingEvents] = useState([]);
  const [meetingRecordings, setMeetingRecordings] = useState([]);
  const [meetingEventsLoading, setMeetingEventsLoading] = useState(false);
  const [meetingRecordingsLoading, setMeetingRecordingsLoading] = useState(false);
  const toastSeq = useRef(0);
  const actorId = currentEmployeeId(data);

  // Toast notifications: each save fires a transient, self-dismissing toast that
  // stacks — so repeated edits to the same field each confirm, and the newest is
  // always on top. No manual dismissal needed (click to close early).
  const pushToast = useCallback((message, tone = "success") => {
    // A toast is drawn as text; an error body (an object) must never reach the screen as one, or
    // React throws and the whole lead page goes blank.
    const text = flashText(message, "");
    if (!text) return;
    const id = (toastSeq.current += 1);
    setToasts((list) => [...list.slice(-3), { id, message: text, tone }]);
    window.setTimeout(() => setToasts((list) => list.filter((item) => item.id !== id)), 3000);
  }, []);
  const dismissToast = (id) => setToasts((list) => list.filter((item) => item.id !== id));

  const loadMeetingProof = useCallback(async () => {
    if (!lead?.id) return;
    setMeetingEventsLoading(true);
    setMeetingRecordingsLoading(true);
    const loadEvents = (addonOn("crm.calendar") ? apiGet(`/Banao/leads/${lead.id}/calendar-events/?lite=1&limit=100`) : Promise.resolve([]))
      .then((payload) => setMeetingEvents(Array.isArray(payload) ? payload : payload?.results || []))
      .catch((error) => {
        setMeetingEvents([]);
        pushToast(error?.payload?.message || error?.message || "Unable To Load Calendar Meetings.", "error");
      })
      .finally(() => setMeetingEventsLoading(false));
    const loadRecordings = (addonOn("crm.recordings") ? apiGet(`/Banao/leads/${lead.id}/meet-recordings/?lite=1&limit=100`) : Promise.resolve([]))
      .then((payload) => setMeetingRecordings(Array.isArray(payload) ? payload : payload?.results || []))
      .catch((error) => {
        setMeetingRecordings([]);
        pushToast(errorText(error, "Unable To Load Meeting Recordings."), "error");
      })
      .finally(() => setMeetingRecordingsLoading(false));
    await Promise.all([loadEvents, loadRecordings]);
  }, [lead?.id, pushToast]);

  useEffect(() => {
    loadMeetingProof();
  }, [loadMeetingProof]);

  useEffect(() => {
    const fresh = buildLeadDraft(lead, data.leadTags || []);
    setDraft(fresh);
    savedDraft.current = fresh;
    setChecklistInput("");
    setNote({ body: "", channelStatus: {} });
    setMeetingToolsOpen(false);
    // Only reset the form + note when we switch to a DIFFERENT lead (by id). Depending on the `lead` /
    // `data.leadTags` object identity re-ran this on every background refresh/re-render and wiped the note
    // textarea while the user was still typing (root cause of "notes disappear" + "can't save note").
  }, [lead.id]);

  // Writes the changed fields of nextDraft. Nothing changed: no request (a blur on an unchanged box
  // used to write, and audit, the whole lead).
  const patchChanged = async (nextDraft) => {
    const payload = changedLeadPayload(lead, savedDraft.current, nextDraft);
    const changed = Object.keys(payload).length > 0;
    if (changed) await apiPatch(`/Banao/LeadAccounts/${lead.id}/`, payload);
    savedDraft.current = nextDraft;
    return changed;
  };

  const saveLead = async (nextDraft = draft, successMessage = "Saved") => {
    setSaving(true);
    try {
      const wrote = await patchChanged(nextDraft);
      setDraft(nextDraft);
      if (wrote) {
        pushToast(successMessage, "success");
        refresh();
      }
    } catch (error) {
      pushToast(error?.payload?.detail || error?.payload?.message || error?.data?.detail || error?.message || "Couldn't save — try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Cycle a fixed-choice field (product / channel) through its options — powers
  // the click-to-change badge + icon in the header. Wraps; unset starts at first.
  const cycleField = (field, options, label) => {
    const index = options.indexOf(draft[field] || "");
    const next = options[(index + 1) % options.length];
    const nextDraft = { ...draft, [field]: next };
    setDraft(nextDraft);
    saveLead(nextDraft, `${label} → ${next}`);
  };
  const setField = (field, value, message) => {
    const nextDraft = { ...draft, [field]: value };
    setDraft(nextDraft);
    saveLead(nextDraft, message);
  };

  // Email / phone live on the lead's primary LeadContact (a separate record),
  // not the lead row — so edit that contact, or create one if none exists yet
  // (Ankit's case: no email in the system). Tenant is attached server-side.
  //
  // Unlike every other inline field (which renders from the local `draft`),
  // email/phone render from the lead prop, which only changes after a refetch —
  // so on a slow/failing refetch the saved value never appeared on screen.
  // `contactOverrides` shows the confirmed-saved value immediately; the next
  // successful refetch carries the same value and takes over.
  const [contactOverrides, setContactOverrides] = useState({});
  useEffect(() => { setContactOverrides({}); }, [lead?.id]);
  const updateContact = async (field, value, label) => {
    setSaving(true);
    try {
      const contacts = lead.contacts || [];
      const primary = contacts.find((c) => c.is_primary) || contacts[0];
      if (primary?.id) {
        await apiPatch(`/Banao/LeadContacts/${primary.id}/`, { [field]: value });
      } else {
        await apiPost("/Banao/LeadContacts/", {
          lead: lead.id,
          name: (lead.leadName && lead.leadName !== "-" && lead.leadName)
            || (lead.companyName && lead.companyName !== "-" && lead.companyName)
            || "Primary Contact",
          is_primary: true,
          [field]: value,
        });
      }
      setContactOverrides((prev) => ({ ...prev, [field]: value }));
      pushToast(`${label} saved`, "success");
      refresh();
    } catch (error) {
      pushToast(error?.payload?.[field]?.[0] || error?.payload?.detail || error?.message || `Couldn't save ${label.toLowerCase()}.`, "error");
    } finally {
      setSaving(false);
    }
  };

  // Campaign is a FK edited by name — the serializer get-or-creates it in the
  // tenant. Sent on its own PATCH (not in the lead payload) so cycling the channel
  // never touches it, and clearing it stays explicit.
  const updateCampaign = async (value) => {
    setSaving(true);
    try {
      await apiPatch(`/Banao/LeadAccounts/${lead.id}/`, { campaign_name: value });
      pushToast(value ? `Campaign → ${value}` : "Campaign cleared", "success");
      refresh();
    } catch (error) {
      pushToast(error?.payload?.detail || error?.message || "Couldn't save campaign.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateChecklist = (nextChecklist, message) => {
    const nextDraft = { ...draft, checklist: nextChecklist };
    setDraft(nextDraft);
    saveLead(nextDraft, message);
  };

  const addChecklistItem = async () => {
    const label = checklistInput.trim();
    if (!label) return;
    const nextChecklist = [...draft.checklist, { id: `task-${Date.now()}`, label, done: false }];
    setChecklistInput("");
    await updateChecklist(nextChecklist, "Checklist Updated.");
  };

  const submitNote = async () => {
    if (!note.body.trim()) {
      pushToast("Write something before adding the note.", "error");
      return;
    }
    setNoteBusy(true);
    try {
      const channelStatus = Object.fromEntries(Object.entries(note.channelStatus).filter(([, value]) => value));
      const enabledChannels = Object.keys(channelStatus);
      const nextDraft = { ...draft, lastUpdateNote: note.body.trim() };
      // Note titles are deprecated — a note is a single body. Title sent blank.
      await apiPost(`/Banao/LeadAccounts/${lead.id}/add-note/`, {
        title: "",
        body: note.body.trim(),
        metadata: {
          channels: enabledChannels,
          channel_status: channelStatus,
          next_follow_up_at: toDateTimePayload(draft.nextFollowUpAt),
        },
      });
      // The note is saved from here on: clear the box so it cannot be added twice, then update the
      // lead's latest comment. If only that second write fails, say so; the note itself is there.
      setNote({ body: "", channelStatus: {} });
      try {
        await patchChanged(nextDraft);
        setDraft(nextDraft);
        pushToast("Note added", "success");
      } catch {
        pushToast("Note added, but the lead's latest update could not be saved.", "error");
      }
      refresh();
    } catch (error) {
      pushToast(error?.payload?.detail || error?.payload?.message || error?.data?.detail || error?.message || "Couldn't save the note.", "error");
    } finally {
      setNoteBusy(false);
    }
  };

  const latestProposal = lead.proposals[0];
  const latestAudit = lead.audits[0];
  const contactVisible = lead.contactVisible !== false;
  const hasValue = (v) => v && v !== "-" && v !== "N/A";
  // Who wrote a note. Prefer server name, else map author id -> employee (even if
  // inactive), else any legacy name in metadata, else the name the importer baked
  // into the legacy title, else "System import" (old rows with no author at all).
  const noteAuthor = (n) => {
    if (n.author_name) return n.author_name;
    const aid = n.author ?? n.author_id;
    if (aid != null) {
      const emp = (data.employees || []).find((e) => String(e.id) === String(aid));
      if (emp) return emp.display_name;
    }
    const m = n.metadata || {};
    if (m.author || m.author_name || m.updated_by) return m.author || m.author_name || m.updated_by;
    const legacy = parseLegacyNoteAuthor(n.title);
    if (legacy) return legacy;
    return "";  // genuinely unknown (true legacy import) — render nothing, not a scary label
  };
  const noteAuthorFirst = (n) => firstNameOf(noteAuthor(n));

  return (
    <section className="Lms-Screen Lms-Detail-Screen">
      <div className="Lms-Breadcrumb">
        <strong>Intranet</strong>
        <button onClick={() => navigate?.("/home/")}>Home</button>
        <span>/</span>
        <button onClick={() => navigate?.("/lms/")}>Leads</button>
        <span>/</span>
        <button onClick={() => navigate?.("/lms/")}>Sales CRM</button>
        <span>/</span>
        <b>{lead.id}</b>
      </div>

      <section className="Lms-Detail-Shell">
        <div className="Lms-Detail-Topbar">
          <span style={{ marginLeft: "auto", fontSize: 12, color: saving ? "var(--indigo-500)" : "var(--c-400)", fontWeight: 500 }}>
            {saving ? "Saving…" : "✓ Changes save automatically"}
          </span>
        </div>

        <header className="Lms-Detail-Header">
          <div className="Lms-Detail-Profile">
            {(() => {
              const score = computeQualityScore(lead);
              const tone = qualityTone(score);
              const label = score < 40 ? "Low — missing key data" : score < 70 ? "Medium — good progress" : "High — well-documented";
              return <LeadQualityRing score={score} tone={tone} title={`Lead quality: ${score}/100 · ${label}`} />;
            })()}
            <div>
              {/* Title = "Client - Company". The client name is what a BA scans
                  for; the company rides along at lower visual weight and is
                  inline-editable like every other field (Set Company Name). */}
              <h1 className="Lms-Detail-Title">
                <span className="Lms-Title-Name">{lead.leadName}</span>
                <span className="Lms-Title-Sep" aria-hidden="true">-</span>
                <EditableField value={hasValue(draft.companyName) ? draft.companyName : ""} placeholder="Set Company Name" ariaLabel="Company Name"
                  onSave={(value) => setField("companyName", value.trim(), value.trim() ? `Company → ${value.trim()}` : "Company cleared")} />
              </h1>
              <p className="Lms-Detail-Meta">
                <button type="button" className="Lms-Meta-Cycle" onClick={() => cycleField("product", PRODUCT_OPTIONS, "Product")}
                  title="Click to change product">
                  <ProductBadge product={draft.product} /> {draft.product || "Set product"}
                </button>
                <button type="button" className="Lms-Meta-Cycle" onClick={() => cycleField("channel", CHANNEL_OPTIONS, "Channel")}
                  title="Click to change channel">
                  <ChannelIcon channel={draft.channel} campaign={lead.campaignName} /> {draft.channel || "Set channel"}
                  {lead.campaignName ? <span className="Lms-Meta-Sub"> · {lead.campaignName}</span> : null}
                </button>
                {hasValue(lead.ownerName) && <span className="Lms-Meta-Item">Owner: {lead.ownerName}</span>}
              </p>
              <small>Created At: {formatDate(lead.createdAt)} | Updated At: {formatDate(lead.updatedAt)}</small>
            </div>
          </div>
          <div className="Lms-Detail-Actions">
            <button
              type="button"
              className={draft.important ? "Lms-Detail-ActionActive" : "Lms-Detail-Action"}
              title={draft.important ? "Marked As Important — Click To Unmark" : "Mark As Important"}
              onClick={() => saveLead({ ...draft, important: !draft.important },
                draft.important ? "Lead Unmarked As Important." : "Lead Marked As Important.")}
            >
              <Star size={18} fill={draft.important ? "currentColor" : "none"} />
            </button>
            <select
              value={draft.stageLabel}
              title="Workflow Status"
              style={{ border: "1px solid var(--c-200)", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, background: "var(--surface)", color: "var(--c-900)", cursor: "pointer" }}
              onChange={(event) => {
                const nextDraft = { ...draft, stageLabel: event.target.value };
                setDraft(nextDraft);
                saveLead(nextDraft, "Workflow Status Updated.");
              }}
            >
              {stageOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            {addonOn("crm.research") && <ResearchProspectButton lead={lead} />}
            <button
              type="button"
              className="Lms-Detail-Action"
              style={{ color: "var(--red-500)" }}
              title="Delete Lead"
              onClick={async () => {
                // Same two-step as the list: archive a live lead, purge one
                // that is already archived.
                const archived = lead.isArchived || lead.is_active === false;
                const ok = window.confirm(archived
                  ? "Permanently delete this archived lead? This cannot be undone."
                  : "Archive this lead? It leaves the pipeline but stays findable by search.");
                if (!ok) return;
                try {
                  if (archived) await apiDelete(`/Banao/LeadAccounts/${lead.id}/`);
                  else await apiPatch(`/Banao/LeadAccounts/${lead.id}/`, { is_active: false });
                } catch (error) {
                  // Stay on the lead: leaving would read as "it worked".
                  pushToast(errorText(error, archived ? "Couldn't delete this lead." : "Couldn't archive this lead."), "error");
                  return;
                }
                navigate?.("/lms/");
              }}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </header>

        {createPortal(
          <div className="Lms-Toasts" role="status" aria-live="polite">
            {toasts.map((item) => (
              <button key={item.id} type="button" className={`Lms-Toast ${item.tone}`} onClick={() => dismissToast(item.id)} title="Dismiss">
                <span className="Lms-Toast-Dot" />
                <span className="Lms-Toast-Msg">{item.message}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}

        <LeadCallContextPanel
          lead={lead}
          onFlash={pushToast}
          events={meetingEvents}
          recordings={meetingRecordings}
          notes={lead.notes || []}
          eventsLoading={meetingEventsLoading}
          recordingsLoading={meetingRecordingsLoading}
          onReload={loadMeetingProof}
        />

        <div className="Lms-Detail-Grid">
          <section className="Lms-Detail-Card Lms-Detail-Main">
            <h3>Log an update</h3>
            <div className="Lms-Note-Editor">
              <NoteChannelChips value={note.channelStatus} onChange={(channelStatus) => setNote({ ...note, channelStatus })} />
              <textarea rows={3} value={note.body} onChange={(event) => setNote({ ...note, body: event.target.value })} placeholder={"Add a note only if it was a client touchpoint (no internal updates).\nWhat happened? — call outcome, objection, next step, what was agreed…"} />
              <details className="Lms-Collapse" onToggle={(event) => { if (event.currentTarget.open) setMeetingToolsOpen(true); }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "var(--c-500)", padding: "2px 0", userSelect: "none" }}>📅 Calendar &amp; Meeting Recording (Optional)</summary>
                {meetingToolsOpen && (
                  <div style={{ paddingTop: 8 }}>
                    <LeadCalendarPanel lead={lead} employees={baOptions.length ? baOptions : data.employees || []} actorId={actorId} onFlash={pushToast} onRefresh={refresh} events={meetingEvents} eventsLoading={meetingEventsLoading} onMeetingDataRefresh={loadMeetingProof} />
                    <MeetRecorderPanel lead={lead} onFlash={pushToast} onRefresh={refresh} recordings={meetingRecordings} loading={meetingRecordingsLoading} onMeetingDataRefresh={loadMeetingProof} />
                  </div>
                )}
              </details>
              <div className="Lms-Note-Footer" style={{ alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600, color: "var(--c-500)", whiteSpace: "nowrap" }}>
                  Next Follow Up
                  <InlineDatePicker
                    value={draft.nextFollowUpAt}
                    min={getTodayDateString()}
                    ariaLabel="Next follow up date"
                    onSelect={(selectedDate) => {
                      const nd = { ...draft, nextFollowUpAt: selectedDate };
                      setDraft(nd);
                      saveLead(nd, selectedDate ? "Follow-up set" : "Follow-up cleared");
                    }}
                  />
                </label>
                <button className="Primary-Button" disabled={noteBusy} onClick={submitNote}>{noteBusy ? "Saving…" : "Add Note"}</button>
              </div>
            </div>

            {(() => {
              const notes = lead.notes || [];
              const tiered = notes.map((n) => ({ note: n, tier: classifyNote(n) }));
              // Badge the most-recent meeting as "last real contact" (notes come
              // newest-first from the API).
              const lastMeetingId = tiered.find((t) => t.tier === "meeting")?.note?.id;
              return (
                <div className="Lms-Note-Stream Lms-Timeline">
                  {tiered.map(({ note: item, tier }) => {
                    const when = item.created_at || item.updated_at;
                    const who = noteAuthorFirst(item);
                    if (tier === "dead") {
                      return (
                        <div key={item.id} className="Lms-TL-Item">
                          <span className="Lms-TL-Node dead" title="No answer / dead touch" />
                          <div className="Lms-TL-Dead">
                            <span className="txt">{noteOneLiner(item)}{item.metadata?.next_follow_up_at ? ` · follow-up → ${formatDate(item.metadata.next_follow_up_at)}` : ""}</span>
                            <span className="meta">{who && <span className="who">{who}</span>}<span className="when" title={dateFullHover(when)}>{dateNoYear(when)}</span></span>
                          </div>
                        </div>
                      );
                    }
                    if (tier === "call") {
                      const rec = noteRecordingLink(item);
                      const action = firstActionItem(item);
                      const fullSummary = item.metadata?.recording_summary || "";
                      return (
                        <div key={item.id} className="Lms-TL-Item">
                          {/* A call with a recording turns its timeline node into a play
                              button (the tick is only for a call with no recording). */}
                          {rec ? (
                            <a className="Lms-TL-Node call play" href={rec} target="_blank" rel="noreferrer" title="Listen to recording">▶</a>
                          ) : (
                            <span className="Lms-TL-Node call" title="Connected touchpoint">✓</span>
                          )}
                          <div className="Lms-TL-Call">
                            <div className="main">
                              <span className="txt" title={fullSummary || undefined}>{hoverOneLiner(item)}</span>
                              {action && <span className="next" title={action}>{action}</span>}
                            </div>
                            <span className="meta">
                              {rec && <a className="rec-icon" href={rec} target="_blank" rel="noreferrer" title="Listen to recording">▶<span className="lbl">Listen recording</span></a>}
                              {who && <span className="who">{who}</span>}
                              <span className="when" title={dateFullHover(when)}>{dateNoYear(when)}</span>
                            </span>
                          </div>
                        </div>
                      );
                    }
                    const rec = noteRecordingLink(item);
                    const isLast = item.id === lastMeetingId;
                    const bodyText = cleanNoteBody(item, Boolean(rec));
                    return (
                      <div key={item.id} className="Lms-TL-Item">
                        <span className="Lms-TL-Node meeting" title="Meeting">◆</span>
                        <div className="Lms-TL-Meeting">
                          <div className="eyebrow"><span className="lbl">Meeting{isLast ? " · last real contact" : ""}</span><span className="when" title={dateFullHover(when)}>{dateNoYear(when)}</span></div>
                          <p className="body">{bodyText || "No summary available yet"}</p>
                          <div className="foot">
                            {who && <span className="who">{who}</span>}
                            {item.metadata?.next_follow_up_at && <span className="fu">follow-up {formatDate(item.metadata.next_follow_up_at)}</span>}
                            {rec && <a className="rec rec-icon meeting" href={rec} target="_blank" rel="noreferrer" title="Watch recording">▶<span className="lbl">Watch recording</span></a>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {lead.message && lead.message !== "-" && (
                    <div className="Lms-TL-Item">
                      <span className="Lms-TL-Node origin" title="Inbound" />
                      <div className="Lms-TL-Origin">
                        <span className="tag">How this lead came in</span>
                        <p>{lead.message}</p>
                      </div>
                    </div>
                  )}
                  {!notes.length && !(lead.message && lead.message !== "-") && <EmptyState label="No updates yet — log the first one above." />}
                </div>
              );
            })()}
          </section>

          <aside className="Lms-Detail-Card Lms-Detail-Side">
            {/* 1. Pipeline — the fields a BA changes on every touch. */}
            <h3>Pipeline</h3>
            <div className="Lms-About-Grid Lms-Grid-Fields">
              <label className="Lms-About-Label" htmlFor="ld-owner">Owner</label>
              <select
                id="ld-owner"
                className="Lms-Grid-Control"
                value={draft.ownerId}
                onChange={(event) => {
                  const nextDraft = { ...draft, ownerId: event.target.value };
                  setDraft(nextDraft);
                  saveLead(nextDraft, "Assignee updated");
                }}
              >
                <option value="">No One Assigned</option>
                {baOptions.map((employee) => <option key={employee.id} value={employee.id}>{employee.display_name}</option>)}
              </select>

              <label className="Lms-About-Label" htmlFor="ld-value">Est. Value</label>
              <input
                id="ld-value"
                className="Lms-Grid-Control"
                type="text"
                inputMode="numeric"
                value={draft.estimatedValue}
                onChange={(event) => setDraft({ ...draft, estimatedValue: moneyInput(event.target.value) })}
                onBlur={() => saveLead(draft, "Estimated value saved")}
                onKeyDown={(e) => { if (e.key === "Enter") { saveLead(draft, "Estimated value saved"); e.target.blur(); } }}
                onWheel={(e) => e.target.blur()}
                placeholder="0.00"
              />

              <label className="Lms-About-Label" htmlFor="ld-upfront">Upfront</label>
              <input
                id="ld-upfront"
                className="Lms-Grid-Control"
                type="text"
                inputMode="decimal"
                value={draft.upfrontValue}
                onChange={(event) => setDraft({ ...draft, upfrontValue: moneyInput(event.target.value) })}
                onBlur={() => saveLead(draft, "Upfront value saved")}
                onKeyDown={(e) => { if (e.key === "Enter") { saveLead(draft, "Upfront value saved"); e.target.blur(); } }}
                onWheel={(e) => e.target.blur()}
                placeholder="0.00"
                title="Cash committed at close. Required before Closed - Won."
              />

              <label className="Lms-About-Label" htmlFor="ld-deal">Deal size</label>
              <input
                id="ld-deal"
                className="Lms-Grid-Control"
                type="text"
                inputMode="decimal"
                value={draft.dealSize}
                onChange={(event) => setDraft({ ...draft, dealSize: moneyInput(event.target.value) })}
                onBlur={() => saveLead(draft, "Deal size saved")}
                onKeyDown={(e) => { if (e.key === "Enter") { saveLead(draft, "Deal size saved"); e.target.blur(); } }}
                onWheel={(e) => e.target.blur()}
                placeholder="0.00"
                title="Strategic size used for open pipeline, not cash at close."
              />
            </div>

            <div className="Lms-Detail-Side-Divider" />

            {/* 2. Contact — the assigned BA edits email/phone inline (add when missing);
                everyone else sees masked, read-only values. */}
            <h3>Contact</h3>
            {contactVisible ? (
              <div className="Lms-About-Grid">
                <span className="Lms-About-Label">Email</span>
                <EditableField value={contactOverrides.email ?? (hasValue(lead.email) ? lead.email : "")} placeholder="Add email" ariaLabel="Email"
                  onSave={(value) => updateContact("email", value, "Email")} />

                <span className="Lms-About-Label">Phone</span>
                <EditableField value={contactOverrides.phone ?? (hasValue(lead.phone) ? lead.phone : "")} placeholder="Add phone" ariaLabel="Phone"
                  onSave={(value) => updateContact("phone", value, "Phone")} />

                {hasValue(lead.websiteUrl) && (<><span className="Lms-About-Label">Website</span><span className="Lms-About-Static"><a href={lead.websiteUrl} target="_blank" rel="noreferrer">{lead.websiteUrl}</a></span></>)}
                {hasValue(lead.url) && (<><span className="Lms-About-Label">Lead URL</span><span className="Lms-About-Static"><a href={lead.url} target="_blank" rel="noreferrer">{lead.url}</a></span></>)}
              </div>
            ) : (
              <div className="Lms-Info-Block">
                {hasValue(lead.email) && (<><label>Email</label><p>{lead.email}</p></>)}
                {hasValue(lead.phone) && (<><label>Phone No.</label><p>{lead.phone}</p></>)}
                {!hasValue(lead.email) && !hasValue(lead.phone) && <p className="Lms-Muted">No Contact Details Yet.</p>}
                <p className="Lms-Muted" style={{ fontSize: 11.5 }}>Full contact details are visible only to the assigned BA.</p>
              </div>
            )}

            <div className="Lms-Detail-Side-Divider" />

            {/* Call Tracking panel removed 2026-07-15 — call outcome now lives on
                the note (channel chips in "Log an update"), a single source. */}

            {/* Tasks on this lead. */}
            {(() => {
              const done = draft.checklist.filter((entry) => entry.done).length;
              const total = draft.checklist.length;
              return (
                <div className="Lms-Checklist">
                  <div className="Lms-Checklist-Head">
                    <h3>Action Items</h3>
                    {total > 0 && <span className="Lms-Checklist-Count">{done}/{total} done</span>}
                  </div>
                  <div className="Lms-Checklist-List">
                    {draft.checklist.map((item) => (
                      <label key={item.id} className={`Lms-Task${item.done ? " is-done" : ""}`}>
                        <input type="checkbox" checked={item.done} onChange={() => updateChecklist(draft.checklist.map((entry) => entry.id === item.id ? { ...entry, done: !entry.done } : entry), item.done ? "Task reopened" : "Task done")} />
                        <span className="Lms-Task-Label">{item.label}</span>
                        <button className="Lms-Task-Del" title="Remove task" onClick={(event) => { event.preventDefault(); updateChecklist(draft.checklist.filter((entry) => entry.id !== item.id), "Task removed"); }}><Trash2 size={13} /></button>
                      </label>
                    ))}
                    {!total && <p className="Lms-Muted Lms-Checklist-Empty">Add any action items for the Owner</p>}
                  </div>
                  <div className="Lms-Checklist-Add">
                    <input
                      value={checklistInput}
                      onChange={(event) => setChecklistInput(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") addChecklistItem(); }}
                      placeholder="Add a task and press Enter"
                    />
                    <button className="Lms-Task-AddBtn" title="Add task" disabled={!checklistInput.trim()} onClick={addChecklistItem}><Plus size={15} /></button>
                  </div>
                </div>
              );
            })()}

            <div className="Lms-Detail-Side-Divider" />

            {/* 5. About — editable facts. Click any value to change it; saves on its own. */}
            <h3>About</h3>
            <div className="Lms-About-Grid">
              <span className="Lms-About-Label">Product</span>
              <EditableField value={draft.product} options={PRODUCT_OPTIONS} placeholder="Set product" ariaLabel="Product"
                onSave={(value) => setField("product", value, value ? `Product → ${value}` : "Product cleared")} />

              <span className="Lms-About-Label">Channel</span>
              <EditableField value={draft.channel} options={CHANNEL_OPTIONS} placeholder="Set channel" ariaLabel="Channel"
                onSave={(value) => setField("channel", value, value ? `Channel → ${value}` : "Channel cleared")} />

              <span className="Lms-About-Label">Campaign</span>
              <EditableField value={lead.campaignName} placeholder="Add campaign" ariaLabel="Campaign"
                onSave={(value) => updateCampaign(value)} />

              <span className="Lms-About-Label">Company</span>
              <EditableField value={draft.companyName} placeholder="Add company" ariaLabel="Company name"
                onSave={(value) => setField("companyName", value, "Company updated")} />

              <span className="Lms-About-Label">Industry</span>
              <EditableField value={draft.industry} placeholder="Add industry" ariaLabel="Industry"
                onSave={(value) => setField("industry", value, "Industry updated")} />
            </div>

            {(latestProposal || latestAudit) && (
              <>
                <div className="Lms-Detail-Side-Divider" />
                <div className="Lms-About-Grid">
                  {latestProposal && (<><span className="Lms-About-Label">Latest Proposal</span><span className="Lms-About-Static">{`${latestProposal.title || "Proposal"} · ${latestProposal.status || "Draft"}`}</span></>)}
                  {latestAudit && (<><span className="Lms-About-Label">Latest Audit</span><span className="Lms-About-Static">{`${latestAudit.title || "Audit"} · ${latestAudit.status || "Open"}`}</span></>)}
                </div>
              </>
            )}
          </aside>
        </div>
      </section>

    </section>
  );
}

function compactList(values = []) {
  return values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
}

function LeadCalendarPanel({ lead, employees = [], actorId, onFlash, onRefresh, events = [], eventsLoading = false, onMeetingDataRefresh }) {
  const defaultTimes = useMemo(() => dateTimeInputFromNow(60, 30), [lead.id]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState({});
  const [form, setForm] = useState(() => ({
    provider: "local",
    title: `Discovery Call - ${lead.companyName || lead.leadName || "Lead"}`,
    startsAt: defaultTimes.startsAt,
    endsAt: defaultTimes.endsAt,
    ownerId: String(lead.ownerId || actorId || ""),
    attendeeIds: Array.from(new Set([lead.ownerId, actorId].filter(Boolean).map(String))),
    description: "",
    externalAttendees: lead.email && lead.email !== "-" ? lead.email : "",
  }));

  const activeEmployees = useMemo(() => (employees || []).filter((item) => item?.id), [employees]);

  const loadCalendar = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const connectionPayload = await apiGet("/Banao/calendar/connections/");
      setConnections(Array.isArray(connectionPayload) ? connectionPayload : []);
    } catch (error) {
      onFlash?.(error?.payload?.message || error?.message || "Unable To Load Calendar.");
    } finally {
      setLoading(false);
    }
  }, [lead?.id, onFlash]);

  const refreshCalendarData = useCallback(async () => {
    await Promise.all([loadCalendar(), onMeetingDataRefresh?.()]);
  }, [loadCalendar, onMeetingDataRefresh]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    const nextTimes = dateTimeInputFromNow(60, 30);
    setForm({
      provider: "local",
      title: `Discovery Call - ${lead.companyName || lead.leadName || "Lead"}`,
      startsAt: nextTimes.startsAt,
      endsAt: nextTimes.endsAt,
      ownerId: String(lead.ownerId || actorId || ""),
      attendeeIds: Array.from(new Set([lead.ownerId, actorId].filter(Boolean).map(String))),
      description: "",
      externalAttendees: lead.email && lead.email !== "-" ? lead.email : "",
    });
    setAvailability(null);
  }, [lead.id, lead.companyName, lead.leadName, lead.ownerId, actorId]);

  const selectedEmployeeIds = form.attendeeIds.map((value) => Number(value)).filter(Boolean);

  const connectCalendar = async (provider) => {
    const label = provider === "local" ? "Local" : provider === "google" ? "Google" : "Outlook";
    const employee = Number(actorId || form.ownerId);
    if (!employee) {
      onFlash?.("Select An Owner Before Connecting A Calendar.");
      return;
    }
    setBusy(true);
    try {
      if (provider === "google") {
        const next = `${window.location.pathname}${window.location.search || ""}`;
        const payload = await apiGet(`/Banao/calendar/google/connect/?employee=${employee}&next=${encodeURIComponent(next)}`);
        if (payload?.authorization_url) {
          window.location.href = payload.authorization_url;
          return;
        }
      }
      const payload = await apiPost("/Banao/calendar/connect/", {
        employee,
        provider,
        calendar_email: "",
        calendar_id: "",
        metadata: { source: "lms_lead_detail" },
      });
      onFlash?.(payload.sync_status === "connected" ? `${label} Calendar Connected.` : payload.sync_error || `${label} Calendar Needs OAuth Credentials.`);
      await refreshCalendarData();
    } catch (error) {
      onFlash?.(error?.payload?.permission || error?.payload?.employee || error?.message || "Unable To Connect Calendar.");
    } finally {
      setBusy(false);
    }
  };

  const checkAvailability = async () => {
    if (!selectedEmployeeIds.length) {
      onFlash?.("Select At Least One Sales/BA Attendee.");
      return;
    }
    setBusy(true);
    try {
      const payload = await apiPost("/Banao/calendar/availability/", {
        employee_ids: selectedEmployeeIds,
        starts_at: form.startsAt,
        ends_at: form.endsAt,
      });
      setAvailability(payload);
      onFlash?.(payload.available ? "Everyone Is Available For This Slot." : "Conflict Found. Pick Another Slot Or Allow Conflict From Backend.");
    } catch (error) {
      const payload = error?.payload;
      if (payload?.conflicts?.length || typeof payload?.available === "boolean") {
        setAvailability(payload);
      } else {
        setAvailability(null);
      }
      onFlash?.(error?.payload?.message || error?.message || "Unable To Check Availability.");
    } finally {
      setBusy(false);
    }
  };

  const scheduleMeeting = async () => {
    if (!form.startsAt || !form.endsAt || !selectedEmployeeIds.length) {
      onFlash?.("Add Start/End Time And At Least One Attendee.");
      return;
    }
    setBusy(true);
    try {
      await apiPost(`/Banao/leads/${lead.id}/calendar-events/`, {
        title: form.title,
        description: form.description,
        owner_id: form.ownerId ? Number(form.ownerId) : null,
        attendee_ids: selectedEmployeeIds,
        external_attendees: form.externalAttendees.split(",").map((item) => item.trim()).filter(Boolean),
        starts_at: form.startsAt,
        ends_at: form.endsAt,
        timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
        sync_provider: form.provider,
        conference_provider: form.provider,
      });
      onFlash?.("Meeting Scheduled And Linked To This Lead.");
      setAvailability(null);
      await refreshCalendarData();
      onRefresh?.();
    } catch (error) {
      const conflicts = error?.payload?.conflicts;
      if (conflicts?.length) setAvailability({ available: false, conflicts });
      else setAvailability(null);
      onFlash?.(error?.payload?.message || error?.message || "Unable To Schedule Meeting.");
    } finally {
      setBusy(false);
    }
  };

  const rescheduleEvent = async (event) => {
    const startsAt = window.prompt("New Start Time (YYYY-MM-DDTHH:mm)", toDateTimeInput(event.starts_at));
    if (!startsAt) return;
    const endsAt = window.prompt("New End Time (YYYY-MM-DDTHH:mm)", toDateTimeInput(event.ends_at));
    if (!endsAt) return;
    setBusy(true);
    try {
      await apiPost(`/Banao/calendar/events/${event.id}/reschedule/`, { starts_at: startsAt, ends_at: endsAt });
      onFlash?.("Meeting Rescheduled.");
      await refreshCalendarData();
      onRefresh?.();
    } catch (error) {
      onFlash?.(error?.payload?.message || error?.message || "Unable To Reschedule Meeting.");
    } finally {
      setBusy(false);
    }
  };

  const cancelEvent = async (event) => {
    const reason = window.prompt("Cancellation Reason", "");
    if (reason === null) return;
    setBusy(true);
    try {
      await apiPost(`/Banao/calendar/events/${event.id}/cancel/`, { reason });
      onFlash?.("Meeting Cancelled.");
      await refreshCalendarData();
      onRefresh?.();
    } catch (error) {
      onFlash?.(error?.payload?.message || error?.message || "Unable To Cancel Meeting.");
    } finally {
      setBusy(false);
    }
  };

  const completeEvent = async (event) => {
    setBusy(true);
    try {
      await apiPost(`/Banao/calendar/events/${event.id}/complete/`, { meeting_notes: meetingNotes[event.id] || "" });
      onFlash?.("Meeting Completed And Notes Saved.");
      setMeetingNotes((current) => ({ ...current, [event.id]: "" }));
      await refreshCalendarData();
      onRefresh?.();
    } catch (error) {
      onFlash?.(error?.payload?.message || error?.message || "Unable To Complete Meeting.");
    } finally {
      setBusy(false);
    }
  };

  const connectedProviders = new Set(connections.filter((item) => item.sync_status === "connected").map((item) => item.provider));

  return (
    <div className="Lms-Calendar-Panel">
      <header className="Lms-Calendar-Header">
        <div>
          <h3><Calendar size={17} /> Calendar Sync</h3>
          <p>Sales And BA Meetings, Availability, Conflict Checks, Reminders, Notes, And Sync Status.</p>
        </div>
        <button className="Soft-Button Small" disabled={loading || eventsLoading} onClick={refreshCalendarData}><RefreshCw size={13} /> Refresh</button>
      </header>

      <div className="Lms-Calendar-Connections">
        {["local", "google", "outlook"].map((provider) => {
          const connection = connections.find((item) => item.provider === provider);
          const label = provider === "local" ? "Local" : provider === "google" ? "Google" : "Outlook";
          const statusVal = connection?.sync_status ? connection.sync_status.charAt(0).toUpperCase() + connection.sync_status.slice(1) : "Connect";
          return (
            <button key={provider} type="button" className={`Lms-Calendar-Provider ${connection?.sync_status || ""}`} disabled={busy} onClick={() => connectCalendar(provider)}>
              {provider === "local" ? <Calendar size={15} /> : provider === "google" ? <Video size={15} /> : <Users size={15} />}
              <span>{label}</span>
              <small>{statusVal}</small>
            </button>
          );
        })}
      </div>

      <div className="Lms-Calendar-Form">
        <label>Title
          <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
        </label>
        <label>Provider
          <select value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value })}>
            <option value="local">Local Calendar</option>
            <option value="google">Google Meet</option>
            <option value="outlook">Microsoft Teams</option>
          </select>
        </label>
        <label>Start
          <input type="datetime-local" value={form.startsAt} onChange={(event) => setForm({ ...form, startsAt: event.target.value })} />
        </label>
        <label>End
          <input type="datetime-local" value={form.endsAt} onChange={(event) => setForm({ ...form, endsAt: event.target.value })} />
        </label>
        <label>Owner
          <select value={form.ownerId} onChange={(event) => setForm({ ...form, ownerId: event.target.value })}>
            <option value="">Select Owner</option>
            {activeEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.display_name}</option>)}
          </select>
        </label>
        <label>Sales / BA Attendees
          <select multiple value={form.attendeeIds} onChange={(event) => setForm({ ...form, attendeeIds: Array.from(event.target.selectedOptions).map((option) => option.value) })}>
            {activeEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.display_name}</option>)}
          </select>
        </label>
        <label>Client Emails
          <input value={form.externalAttendees} onChange={(event) => setForm({ ...form, externalAttendees: event.target.value })} placeholder="Client@example.com, Stakeholder@example.com" />
        </label>
        <label>Description
          <textarea rows={3} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </label>
      </div>

      {availability && (
        <div className={`Lms-Calendar-Availability ${availability.available ? "ok" : "conflict"}`}>
          <strong>{availability.available ? "Available" : "Conflicts Found"}</strong>
          {(availability.conflicts || []).map((item, index) => (
            <span key={`${item.event_id || item.source || "conflict"}-${index}`}>
              {item.title || "Busy"} - {formatDateTime(item.starts_at)}
              {item.error ? ` (${item.error})` : ""}
            </span>
          ))}
          {!availability.available && !(availability.conflicts || []).length && <span>No Conflict Details Were Returned. Please Try Again.</span>}
        </div>
      )}

      <div className="Lms-Calendar-Actions">
        <button className="Outline-Button Small" disabled={busy} onClick={checkAvailability}><Clock size={14} /> Check Availability</button>
        <button className="Primary-Button Small" disabled={busy || (form.provider !== "local" && !connectedProviders.has(form.provider))} onClick={scheduleMeeting}>
          <Calendar size={14} /> Schedule Meeting
        </button>
      </div>

      <div className="Lms-Calendar-Events">
        {events.map((event) => (
          <article key={event.id} className={`Lms-Calendar-Event ${event.status}`}>
            <div>
              <strong>{event.title}</strong>
              <span>{formatDateTime(event.starts_at)} - {formatDateTime(event.ends_at)}</span>
              <small>{event.status} | {event.sync_provider} {event.sync_error ? `| ${event.sync_error}` : ""}</small>
              {event.meeting_url && <a href={event.meeting_url} target="_blank" rel="noreferrer">{event.meeting_url}</a>}
            </div>
            <textarea rows={2} value={meetingNotes[event.id] || ""} onChange={(e) => setMeetingNotes({ ...meetingNotes, [event.id]: e.target.value })} placeholder="Meeting Notes" />
            <div className="Lms-Calendar-Event-Actions">
              <button className="Soft-Button Small" disabled={busy || event.status === "cancelled"} onClick={() => rescheduleEvent(event)}>Reschedule</button>
              <button className="Soft-Button Small" disabled={busy || event.status === "cancelled"} onClick={() => cancelEvent(event)}>Cancel</button>
              <button className="Soft-Button Small" disabled={busy || event.status === "completed" || event.status === "cancelled"} onClick={() => completeEvent(event)}><CheckCircle2 size={13} /> Complete</button>
            </div>
          </article>
        ))}
        {!events.length && <div className="Lms-Calendar-Empty">No Calendar Meetings Scheduled For This Lead.</div>}
      </div>
    </div>
  );
}

function flashText(value, fallback = "Something went wrong.") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((item) => flashText(item, "")).filter(Boolean).join(", ") || fallback;
  if (typeof value === "object") return value.message || value.detail || value.error || value.raw || JSON.stringify(value);
  return String(value);
}

function recordingIdFromInput(value = "") {
  const text = String(value || "").trim();
  if (!text) return "";
  const urlMatch = text.match(/meet\.atg\.party\/(?:public\/)?meetings\/([A-Za-z0-9][A-Za-z0-9_-]*)(?=[\s\]\)>,.!?:;"']|$)/i);
  if (urlMatch) return urlMatch[1];
  return /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(text) ? text : "";
}

function MeetRecorderPanel({ lead, onFlash, onRefresh, recordings = [], loading = false, onMeetingDataRefresh }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState("info");
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState("");

  const showMessage = useCallback((text, tone = "info") => {
    const safeText = flashText(text, "");
    setMessage(safeText);
    setMessageTone(tone);
    if (safeText) onFlash?.(safeText);
  }, [onFlash]);

  const searchRecordings = async () => {
    setSearching(true);
    setMessage("");
    try {
      const directRecordingId = recordingIdFromInput(search);
      if (directRecordingId) {
        await apiPost(`/Banao/leads/${lead.id}/meet-recordings/`, {
          recording_id: directRecordingId,
          create_note: false,
        });
        showMessage("Recording attached to this lead.", "success");
        setResults([]);
        setSearch("");
        await onMeetingDataRefresh?.();
        onRefresh?.();
        return;
      }
      const query = new URLSearchParams();
      if (search.trim()) query.set("search", search.trim());
      query.set("status", "ai_ready");
      query.set("page_size", "12");
      const payload = await apiGet(`/Banao/meet-recorder/recordings/?${query.toString()}`);
      const rows = Array.isArray(payload?.results) ? payload.results : Array.isArray(payload) ? payload : [];
      setResults(rows);
      if (!rows.length) showMessage("No AI-ready recorder meetings found.", "info");
    } catch (error) {
      showMessage(error?.payload?.meet_recorder || error?.payload || error?.message || "Unable To Search Meet Recorder.", "error");
    } finally {
      setSearching(false);
    }
  };

  const attachRecording = async (recording) => {
    setBusyId(String(recording.id));
    try {
      await apiPost(`/Banao/leads/${lead.id}/meet-recordings/`, {
        recording_id: String(recording.id),
        create_note: false,
      });
      showMessage("Recording attached to this lead.", "success");
      setResults([]);
      setSearch("");
      await onMeetingDataRefresh?.();
      onRefresh?.();
    } catch (error) {
      showMessage(error?.payload?.meet_recorder || error?.payload?.recording_id || error?.payload || error?.message || "Unable To Attach Recording.", "error");
    } finally {
      setBusyId("");
    }
  };

  const syncRecording = async (recording) => {
    setBusyId(`sync-${recording.id}`);
    try {
      await apiPost(`/Banao/meet-recordings/${recording.id}/sync-ai/`, {});
      showMessage("Recording AI refreshed.", "success");
      await onMeetingDataRefresh?.();
    } catch (error) {
      showMessage(error?.payload?.meet_recorder || error?.payload || error?.message || "Unable To Sync Recording.", "error");
    } finally {
      setBusyId("");
    }
  };

  const createNote = async (recording) => {
    setBusyId(`note-${recording.id}`);
    try {
      await apiPost(`/Banao/meet-recordings/${recording.id}/create-note/`, {});
      showMessage("AI summary saved to lead notes.", "success");
      await onMeetingDataRefresh?.();
      onRefresh?.();
    } catch (error) {
      showMessage(error?.payload?.recording || error?.payload || error?.message || "Unable To Create Note.", "error");
    } finally {
      setBusyId("");
    }
  };

  const addActions = async (recording) => {
    setBusyId(`actions-${recording.id}`);
    try {
      await apiPost(`/Banao/meet-recordings/${recording.id}/add-actions/`, {});
      showMessage("Action items added to lead checklist.", "success");
      onRefresh?.();
    } catch (error) {
      showMessage(error?.payload?.action_items || error?.payload || error?.message || "Unable To Add Action Items.", "error");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="Lms-Recorder-Panel">
      <header className="Lms-Calendar-Header">
        <div>
          <h3><Video size={17} /> Meeting Recordings</h3>
          <p>Attach Meet Recorder AI summaries, action items, and transcripts to this lead.</p>
        </div>
        <button className="Soft-Button Small" disabled={loading} onClick={onMeetingDataRefresh}><RefreshCw size={13} /> Refresh</button>
      </header>

      <div className="Lms-Recorder-Search">
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search recorder meetings..." onKeyDown={(event) => { if (event.key === "Enter") searchRecordings(); }} />
        <button className="Primary-Button Small" disabled={searching} onClick={searchRecordings}><Search size={14} /> Search</button>
      </div>

      {message && <div className={`Lms-Recorder-Message ${messageTone}`}>{message}</div>}

      {!!results.length && (
        <div className="Lms-Recorder-Results">
          {results.map((item) => (
            <article key={item.id} className="Lms-Recorder-Result">
              <div>
                <strong>{item.title || `Recording ${item.id}`}</strong>
                <span>{formatDateTime(item.created_at)} {item.duration_seconds ? `| ${Math.round(item.duration_seconds / 60)} min` : ""}</span>
                <small>{item.meeting_code || item.meeting_url || item.owner_email || "Meet Recorder"}</small>
              </div>
              <button className="Soft-Button Small" disabled={Boolean(busyId)} onClick={() => attachRecording(item)}>
                {busyId === String(item.id) ? "Attaching..." : "Attach"}
              </button>
            </article>
          ))}
        </div>
      )}

      <div className="Lms-Recorder-List">
        {recordings.map((recording) => {
          return (
            <article key={recording.id} className="Lms-Recorder-Card">
              <div className="Lms-Recorder-Card-Top">
                <div>
                  <strong>{recording.title || `Recording ${recording.external_recording_id}`}</strong>
                  <span>{formatDateTime(recordingDate(recording))} {recording.duration_seconds ? `| ${Math.round(recording.duration_seconds / 60)} min` : ""}</span>
                  <small>{recording.ai_status || "unknown"} {recording.meeting_code ? `| ${recording.meeting_code}` : ""}</small>
                </div>
                <div className="Lms-Recorder-Actions">
                  {recording.recording_url && <a className="Soft-Button Small" href={recording.recording_url} target="_blank" rel="noreferrer">Open</a>}
                  <button className="Soft-Button Small" disabled={Boolean(busyId)} onClick={() => syncRecording(recording)}>{busyId === `sync-${recording.id}` ? "Syncing..." : "Sync AI"}</button>
                </div>
              </div>
              <p className="Lms-Recorder-Attachment-Hint">Summary and action items appear once in Call context above.</p>
            </article>
          );
        })}
        {!recordings.length && <div className="Lms-Calendar-Empty">No Meet Recorder summaries attached to this lead.</div>}
      </div>
    </div>
  );
}

function AddLeadModal({ data, bas = [], sources, busy, error, onClose, onSubmit }) {
  // A non-admin only ever has themselves in `bas` (the parent already scopes
  // the owner list that way) — leaving Owner on an unforced "Select Owner"
  // placeholder meant a junior could submit without picking their own name,
  // and the lead would land unowned: invisible from their own "my leads" view.
  // Default it to self so that can't happen. A superuser's full roster is
  // untouched and still starts unset, since which rep it's for is their call.
  const [formData, setFormData] = useState(() => ({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    product: "Banao",
    attr_source: "Website-inbound",
    priority: "Normal",
    owner: data?.me?.user?.isSuperuser ? "" : (currentEmployeeId(data) || ""),
    estimated_value: "",
    upfront_value: "",
    deal_size: "",
    currency: "INR",
    url: "",
    website_url: "",
    metadata: {},
  }));
  const [touched, setTouched] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isValidUrl = (value) => /^https?:\/\/.+\..+/i.test(value);

  // Single source of truth for validation; rendered inline under each field.
  const fieldErrors = {};
  if (!formData.company_name.trim()) fieldErrors.company_name = "Company Name Is Required";
  const missingContact = !formData.contact_email.trim() && !formData.contact_phone.trim();
  if (missingContact) fieldErrors.contact = "At Least One Of Email Or Phone Is Required";
  if (formData.contact_email.trim() && !isValidEmail(formData.contact_email.trim())) fieldErrors.contact_email = "This Email Doesn't Look Valid";
  if (formData.contact_name.trim() && !isValidContactName(formData.contact_name.trim())) fieldErrors.contact_name = "Letters, Spaces, Hyphens And Dots Only";
  if (formData.website_url.trim() && !isValidUrl(formData.website_url.trim())) fieldErrors.website_url = "Must Start With http:// Or https://";
  if (formData.url.trim() && !isValidUrl(formData.url.trim())) fieldErrors.url = "Must Start With http:// Or https://";
  const canSubmit = Object.keys(fieldErrors).length === 0;

  const showError = (field) => Boolean((touched[field] || touched._submit) && fieldErrors[field]);
  const hintFor = (field, fallback = " ") => (showError(field) ? fieldErrors[field] : fallback);

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched((prev) => ({ ...prev, _submit: true }));
    if (!canSubmit) return;
    const payload = {
      company_name: formData.company_name.trim(),
      product: formData.product,
      attr_source: formData.attr_source,
      source: formData.attr_source,
      priority: formData.priority || "Normal",
      currency: formData.currency || "INR",
    };
    if (formData.contact_name.trim()) payload.contact_name = formData.contact_name.trim();
    if (formData.contact_email.trim()) payload.contact_email = formData.contact_email.trim();
    if (formData.contact_phone.trim()) payload.contact_phone = formData.contact_phone.trim();
    if (formData.owner) payload.owner = Number(formData.owner);
    Object.assign(payload, leadCaptureMoneyFields(formData));
    if (formData.url.trim()) payload.url = formData.url.trim();
    if (formData.website_url.trim()) payload.website_url = formData.website_url.trim();
    onSubmit(payload);
  };


  return (
    <Modal title="Add New Lead" onClose={onClose}>
      <form className="AddLead-Form" onSubmit={handleSubmit} noValidate>
        {error && <div className="AddLead-Error-Banner">{error}</div>}
        <div className="Form-Grid Two">
          <div className="Span-2 AddLead-Section">Company</div>
          <label className="Span-2">Company Name <span className="Req">*</span>
            <input autoFocus type="text" value={formData.company_name} onChange={(e) => handleChange("company_name", e.target.value)} placeholder="Enter Company Name"
              className={showError("company_name") ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("company_name") ? " Error" : ""}`}>{hintFor("company_name")}</small>
          </label>
          <label>Product <span className="Req">*</span>
            <select value={formData.product} onChange={(e) => handleChange("product", e.target.value)}>
              {PRODUCT_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <small className="Field-Hint"> </small>
          </label>
          <label>Channel <span className="Req">*</span>
            <select value={formData.attr_source} onChange={(e) => handleChange("attr_source", e.target.value)}>
              {CHANNEL_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <small className="Field-Hint"> </small>
          </label>

          <div className="Span-2 AddLead-Section">Contact</div>
          <label className="Span-2">Contact Name
            <input type="text" value={formData.contact_name} onChange={(e) => handleChange("contact_name", e.target.value)} placeholder="Enter Contact Name"
              className={showError("contact_name") ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("contact_name") ? " Error" : ""}`}>{hintFor("contact_name")}</small>
          </label>
          <label>Email <span className="Req" title="At least one of Email or Phone">†</span>
            <input type="email" value={formData.contact_email} onChange={(e) => handleChange("contact_email", e.target.value)} placeholder="Enter Email"
              className={showError("contact_email") || (showError("contact") && (touched.contact_email || touched.contact_phone || touched._submit)) ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("contact_email") || showError("contact") ? " Error" : ""}`}>
              {showError("contact_email") ? fieldErrors.contact_email : showError("contact") ? fieldErrors.contact : "At Least One Of Email Or Phone"}
            </small>
          </label>
          <label>Phone <span className="Req" title="At least one of Email or Phone">†</span>
            <input type="tel" value={formData.contact_phone} onChange={(e) => handleChange("contact_phone", e.target.value.replace(/[^0-9+\-\s]/g, "").slice(0, 15))} placeholder="Enter Phone Number" maxLength={15}
              className={showError("contact") && (touched.contact_email || touched.contact_phone || touched._submit) ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("contact") ? " Error" : ""}`}>{showError("contact") ? fieldErrors.contact : "At Least One Of Email Or Phone"}</small>
          </label>

          <div className="Span-2 AddLead-Section">Deal Details</div>
          <label>Owner
            <select value={formData.owner} onChange={(e) => handleChange("owner", e.target.value)}>
              <option value="">Select Owner</option>
              {(bas || []).map((emp) => <option key={emp.id} value={emp.id}>{emp.display_name}</option>)}
            </select>
            <small className="Field-Hint"> </small>
          </label>
          <label>Priority
            <select value={formData.priority} onChange={(e) => handleChange("priority", e.target.value)}>
              <option value="Low">Low</option>
              <option value="Normal">Normal</option>
              <option value="High">High</option>
            </select>
            <small className="Field-Hint"> </small>
          </label>
          <label className="Span-2">Estimated Value (INR)
            <input type="text" inputMode="decimal" value={formData.estimated_value}
              onChange={(e) => { const v = moneyInput(e.target.value); handleChange("estimated_value", v); }}
              onWheel={(e) => e.target.blur()} placeholder="0.00" />
            <small className="Field-Hint"> </small>
          </label>
          <label>Upfront value (INR)
            <input type="text" inputMode="decimal" value={formData.upfront_value}
              onChange={(e) => { const v = moneyInput(e.target.value); handleChange("upfront_value", v); }}
              onWheel={(e) => e.target.blur()} placeholder="Cash at close" />
            <small className="Field-Hint">Required before Closed - Won.</small>
          </label>
          <label>Deal size (INR)
            <input type="text" inputMode="decimal" value={formData.deal_size}
              onChange={(e) => { const v = moneyInput(e.target.value); handleChange("deal_size", v); }}
              onWheel={(e) => e.target.blur()} placeholder="Open pipeline" />
            <small className="Field-Hint">Strategic size, not cash at close.</small>
          </label>

          <div className="Span-2 AddLead-Section">Links <span className="AddLead-Section-Optional">(optional)</span></div>
          <label>Website URL
            <input type="text" value={formData.website_url} onChange={(e) => handleChange("website_url", e.target.value)} placeholder="https://example.com"
              className={showError("website_url") ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("website_url") ? " Error" : ""}`}>{hintFor("website_url")}</small>
          </label>
          <label>Lead URL
            <input type="text" value={formData.url} onChange={(e) => handleChange("url", e.target.value)} placeholder="https://example.com/lead"
              className={showError("url") ? "Invalid" : ""} />
            <small className={`Field-Hint${showError("url") ? " Error" : ""}`}>{hintFor("url")}</small>
          </label>
        </div>
        <div className="Modal-Actions">
          <button type="button" className="Outline-Button" onClick={onClose}>Cancel</button>
          <button type="submit" className="Primary-Button" disabled={busy}>{busy ? "Creating..." : "Create Lead"}</button>
        </div>
      </form>
    </Modal>
  );
}

function AssignLeadModal({ bas = [], count, busy, onClose, onSubmit }) {
  const [employeeId, setEmployeeId] = useState("");
  const [confirm, setConfirm] = useState(false);
  const owner = bas.find((e) => String(e.id) === String(employeeId));
  return (
    <Modal title={`Assign ${count} Leads To Owner`} onClose={onClose}>
      {!confirm ? (
        <>
          <p style={{ fontSize: 13, color: "var(--c-500)", marginBottom: 12 }}>This will reassign all {count} selected leads. Their current owners will be overwritten.</p>
          <label>Owner
            <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
              <option value="">Select Owner</option>
              {bas.map((emp) => <option key={emp.id} value={emp.id}>{emp.display_name}</option>)}
            </select>
          </label>
          <button className="Primary-Button" disabled={busy || !employeeId} onClick={() => setConfirm(true)}>Next</button>
        </>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "var(--red-600)", marginBottom: 12 }}>Are you sure you want to reassign all {count} selected leads to <strong>{owner?.display_name}</strong>? Their Current Ownership Will Be Overwritten.</p>
          <div className="Modal-Actions">
            <button className="Outline-Button" onClick={() => setConfirm(false)}>Back</button>
            <button className="Primary-Button" disabled={busy} onClick={() => onSubmit(employeeId)}>Confirm Assign</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function BulkStageModal({ count, busy, onClose, onSubmit, options }) {
  const [picked, setPicked] = useState("");
  const [confirm, setConfirm] = useState(false);
  const stages = options?.length ? options : ["New Lead", "Contact Attempted", "Discovery / Demo Scheduled", "Proposal Sent", "Closed - Won", "Closed - Lost"];
  const stage = shownStage(picked, stages);
  return (
    <Modal title={`Move ${count} Leads To Stage`} onClose={onClose}>
      {!confirm ? (
        <>
          <p style={{ fontSize: 13, color: "var(--c-500)", marginBottom: 12 }}>This Will Update The Workflow Status Of All {count} Selected Leads. Their Current Individual Stages Will Be Overwritten.</p>
          <label>Stage
            <select value={stage} onChange={(event) => setPicked(event.target.value)}>
              {stages.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <button className="Primary-Button" disabled={busy} onClick={() => setConfirm(true)}>Next</button>
        </>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "var(--red-600)", marginBottom: 12 }}>Are You Sure You Want To Move All {count} Selected Leads To <strong>{stage}</strong>? Their Current Workflow Statuses Will Be Overwritten. This Cannot Be Undone.</p>
          <div className="Modal-Actions">
            <button className="Outline-Button" onClick={() => setConfirm(false)}>Back</button>
            <button className="Primary-Button" disabled={busy} onClick={() => onSubmit(stage)}>Confirm Move</button>
          </div>
        </>
      )}
    </Modal>
  );
}

